// The dispatch board (technical design §11 M2): each technician's day is a lane (Riverstone's
// "lanes for a team's day"); visits are pills sized by time; unassigned visits wait in the tray.
// BL-124: drag a visit along the day, to another technician's lane, or by either end (pointer events,
// so a finger works too: hold to pick it up), or move it with the arrow keys; board-drag-model.ts
// decides what letting go means. Tap-to-assign and the detail sheet offer the rest by keyboard.
// Live: the board re-reads itself when the server says visits or jobs changed.

import { liveStream } from './connection/live.ts';
import { gapsText } from './settings/licences-model.ts';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { RpcError } from '@flowology/client';
import { dateText, localDateOf, sentences, timeText } from '@flowology/kernel';
import { client } from './api.ts';
import {
  boardDensity,
  cachedLanes,
  keepDensity,
  keepLanes,
  keepTrayFolds,
  trayFolds,
  type Density,
  type Tray,
  type TrayFolds,
} from './board-cache.ts';
import { openJobPage } from './job/job-page.ts';
import {
  addListPage,
  clockLine,
  listReadSize,
  LIST_EMPTY,
  LIST_PAGE,
  listRows,
  listShownText,
  noTechniciansLine,
  notStartedFor,
  type BoardLists,
  type ListTab,
} from './board-extras-model.ts';
import { AddVisit } from './shared/AddVisit.tsx';
import { scrollFocus } from './shared/scrollFocus.ts';
import { canSee, Header, PLACES, waitingOn, type NavProps } from './Nav.tsx';
import { WeekView } from './WeekView.tsx';
import { MIRROR_LINE, useMirror } from './shared/mirror.ts';
import { isStale } from './shared/rebase.ts';
import './hours/hours.css';
import { DAY_WINDOWS, dayOptions, hourNow, windowLabel, windowStarted, zonedInstant } from './book-model.ts';
import {
  assignInput,
  BOARD_EVENTS,
  clock,
  DAY_END_HOUR,
  DAY_START_HOUR,
  dayEndHour,
  arriveInput,
  busyFromRefusal,
  busyTechs,
  dispatchBlocker,
  queuedFor,
  queueInput,
  queueRefusal,
  officeStep,
  PAUSE_REASONS,
  pauseInput,
  pauseWords,
  pausedGroups,
  pausedSince,
  weekOf,
  filterOptions,
  keeps,
  keepsTech,
  filterSummary,
  movedToLine,
  movedWords,
  moveWindowIndex,
  NO_FILTER,
  type BoardFilter,
  dayVisits,
  nextSort,
  sortedVisits,
  statusCounts,
  type VisitSort,
  type VisitSortKey,
  type PauseReason,
  laneRows,
  lookWhy,
  laneStatus,
  shownStatus,
  workDone,
  laterDay,
  localHours,
  MIN_PILL_PCT,
  needsALook,
  outChip,
  laneAway,
  laneBands,
  overdue,
  overdueWords,
  serviceTitanAsOfText,
  importReadAt,
  overdueTone,
  overlapWarning,
  boardChanges,
  othersChangeTo,
  shownSpan,
  placeResult,
  placement,
  tagInk,
  tagsLabel,
  techMessagesWords,
  windowText,
  OFFLINE_WAITING,
  refusalWords,
  rolledDay,
  type Board,
  type BoardTech,
  type BoardVisit,
  visitStatusLabel,
} from './board-model.ts';
import {
  cantPickUp,
  changedUnderCheck,
  classifyMove,
  DEFAULT_SLOT_MINUTES,
  dragTo,
  footFor,
  givenLine,
  HOLD_MS,
  insideWindow,
  instantOfMinute,
  keyEdge,
  keyStep,
  laneGive,
  minuteAtX,
  minuteOf,
  MOUSE_SLOP_PX,
  type MoveResult,
  noAnswerLine,
  movedLine,
  movePlan,
  noteText,
  retimedLine,
  SAVE_WITHIN_MS,
  shiftedWindow,
  SLOW_SAVE_MS,
  STILL_SAVING,
  STILL_SENDING,
  waitingToSendLine,
  timeRule,
  typedTimes,
  TOUCH_SLOP_PX,
  UNDO_MS,
  undoPlan,
  retimePlan,
  type DragEdge,
  type DragHow,
  type DragPlace,
  type MovePlan,
  type NoteLine,
  type Outcome,
} from './board-drag-model.ts';
import './board-drag.css';
import {
  groupFolds,
  groupLabel,
  keepGroupFolds,
  laneGroups,
  laneOrderOf,
  toggled,
  usuallyLine,
  type LaneGroup,
} from './board-divisions-model.ts';
import { DivisionsSheet, MoveForDaySheet } from './board-divisions.tsx';
import { useBoardGrouping } from './board-grouping.tsx';
import { plainText } from './shared/plain-text.ts';
import {
  Board as DayBoard,
  BoardBand,
  BoardCorner,
  BoardLane,
  focusLaneName,
  Button as FlButton,
  Choice,
  ChoiceGroup,
  ConfirmDialog,
  DivisionGroup,
  DragNote,
  FormDialog,
  JobTable,
  KeyValue,
  Link as FlLink,
  MapStrip,
  SelectField,
  SidePanel,
  StatusBar,
  TextField,
  TechMenu,
  TimeScale,
  Toast,
  dayWord,
  problemCardWords,
  problemLaneWords,
  problemsNoteWords,
  type Density as FlDensity,
  type LaneVisit,
  type MenuAt,
} from '@flowology/ui-web/fl';
import { useDesign } from './blueprint/design.ts';
import { boardFigures, boardTitle } from './blueprint/schedule-model.ts';
import './board-page.css';
import { VISIT_WORDS } from '@flowology/ui-web/words';
import { useOnline } from './connection/connection.ts';
import { phoneText } from './types.ts';
import { QuickEditDialog, type QuickEditOpen } from './timesheet/QuickEditDialog.tsx';
import { dayMapProps, pensBy } from './schedule/pages-model.ts';
import { DayMapOrGoogle } from './schedule/DayMapOrGoogle.tsx';
import {
  bandFigures,
  dayTabVisits,
  DAY_COLUMNS,
  divisionLines,
  fixesHours,
  isListTab,
  LIST_COLUMNS,
  listJobRow,
  LOOK_COLUMNS,
  laneDayLine,
  laneClockWords,
  laneOffOf,
  laneVisitOf,
  panelFields,
  panelNote,
  panelTitle,
  pastState,
  shortName,
  statusScopes,
  PAUSED_COLUMNS,
  pausedTables,
  panelShown,
  penAt,
  PHONE_WIDTH,
  placeholderDay,
  problemsOf,
  routeOf,
  startWord,
  statusGroups,
  techDayOf,
  techMenuRole,
  visitRow,
  visitState,
  type BoardTab,
  type TeamDay,
  type VisitOn,
} from './board-bp-model.ts';

const STATUS_LABEL: Record<string, string> = VISIT_WORDS;
const STATUS_CHIP: Record<string, string> = {
  scheduled: '',
  dispatched: 'brand',
  working: 'brand',
  paused: 'warn',
  closed: 'ok',
};

/** The last openDay the board opened on (its `at`), kept across the board's remounts. */
let openedDayAt = 0;

function localToday(timeZone?: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
/** The loading ladder (docs/design/states-pass.md): nothing new for 300 ms, placeholders, a line at 3 s, the error at 10 s. */
type Phase = 'wait' | 'ph' | 'slow' | 'error';

/**
 * Who missed a clock-out in the last seven days (time.team's leftOpen), for those who correct hours:
 * read on opening the board and every five minutes. Nobody, for anyone else.
 */
function useLeftOpen(wanted: boolean, timeZone?: string): { staffId: string; name: string }[] {
  const [people, setPeople] = useState<{ staffId: string; name: string }[]>([]);
  useEffect(() => {
    if (!wanted) return;
    const read = () => {
      const to = localToday(timeZone);
      client
        .call<{ people: { staffId: string; name: string; leftOpen: number }[] }>('time.team', {
          from: shiftDate(to, -6),
          to,
        })
        .then((r) => setPeople(r.people.filter((p) => p.leftOpen > 0)))
        .catch(() => undefined);
    };
    read();
    const t = setInterval(read, 5 * 60_000);
    return () => clearInterval(t);
  }, [wanted, timeZone]);
  return people;
}

function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** A move's idempotency key (QA-848): one per move, kept for its Try again. */
const newKey = (): string =>
  typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** QA-848: true once `sending` has gone on for `ms` (still waiting for its answer). */
function useSlow(sending: boolean, ms: number): boolean {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    setSlow(false);
    if (!sending) return;
    const t = setTimeout(() => setSlow(true), ms);
    return () => clearTimeout(t);
  }, [sending, ms]);
  return slow;
}

export function BoardScreen({
  nav,
  who,
  canDispatch,
  onOpenHours,
  onOpenTruck,
  openDay,
  hidden = false,
}: {
  nav: NavProps;
  /**
   * Open on this day's board (Blueprint); `at` opens it again for the same day.
   * With a visit (Schedule › Map's), that visit open too.
   */
  openDay?: { date: string; at: number; visit?: string };
  /** The signed-in person's membership: the density this browser remembers for them. */
  who?: string;
  canDispatch: boolean;
  /**
   * For those who correct hours: open a technician's hours (a clock-out missed in the last week), or
   * their timesheet for a day (their clock line, BL-126).
   */
  onOpenHours?: (staffId: string, date?: string) => void;
  /** The tech menu's Truck stock: Inventory, at that truck's stock location (with inventory access). */
  onOpenTruck?: (locationId: string) => void;
  /** BL-128: a job's page is over the board; the board stays as it was under it, for Back. */
  hidden?: boolean;
}) {
  // the company's today (its time zone, not this browser's), which a board left open follows (QA-764)
  const [date, setDate] = useState(() => localToday(nav.timeZone));
  // While the company mirrors ServiceTitan its visits are ServiceTitan's (st-mirror §7): the board
  // reads them, and nothing here moves one, so no drag is refused after the drop.
  const mirroring = useMirror();
  const dispatching = canDispatch && !mirroring;
  // Blueprint (DEC-008) draws the same board as the prototype's day board: pens, ruled lanes, the
  // figures over it and a lane for the visits nobody has yet. What the board does is the same.
  const bp = useDesign() === 'blueprint';
  // dispatch parity 6: the day's lanes, or the week by technician (a day there opens its board)
  const [view, setView] = useState<'day' | 'week'>('day');
  // QA-768: the board opens at its top, never where the last page was scrolled to (Book a visit's
  // foot); opened on a visit (one just booked, the map's), scrolled to that visit once it's drawn
  const scrollToVisit = useRef<string | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    // each one once: coming back to the board later (it remounts) opens on today, not that day again
    if (!openDay || openDay.at === openedDayAt) return;
    openedDayAt = openDay.at;
    setDate(openDay.date);
    setView('day');
    if (openDay.visit) {
      setSelected(openDay.visit);
      scrollToVisit.current = openDay.visit;
    }
  }, [openDay?.date, openDay?.at]);
  const leftOpen = useLeftOpen(!!onOpenHours, nav.timeZone);
  const [board, setBoard] = useState<Board | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  // what a live re-read checks against (QA-847): the open visit, and the visit an Undo would put back
  const selectedRef = useRef<string | null>(null);
  selectedRef.current = selected;
  const undoIdRef = useRef<string | null>(null);
  const undoDropRef = useRef<() => void>(() => {});
  // dispatch parity 5: the status bar's choice (null: every visit) and the visit table's order
  // dispatch parity 7: with a business unit, job type and tag too (filter.status is the bar's)
  const [filter, setFilter] = useState<BoardFilter>(NO_FILTER);
  const only = filter.status;
  const setOnly = (status: string | null) => setFilter({ ...filter, status });
  // a pill's card on hover (a pointer that hovers): the panel's essentials without opening it
  const [hover, setHover] = useState<{ id: string; left: number; top: number; bottom: number } | null>(null);
  // Blueprint: the corner's filter pop-over (BoardCorner's Popover closes it on a press outside or Esc)
  const [filterOpen, setFilterOpen] = useState(false);
  const [canHover] = useState(
    () => typeof matchMedia === 'function' && matchMedia('(hover: hover) and (pointer: fine)').matches,
  );
  // a phone's width (the library's touch breakpoint): the open visit's panel covers the lanes there
  const [phoneWide] = useState(() => typeof matchMedia === 'function' && matchMedia(PHONE_WIDTH).matches);
  // BL-100: Compact lanes, with a mouse only (a touch screen keeps Comfortable's targets), as chosen last
  const [density, setDensity] = useState<Density>(() => boardDensity(nav.tenant, who));
  const compact = canHover && density === 'compact';
  // BL-100: the side column's trays fold from their headings, each as this person left it (open at first)
  const [folds, setFolds] = useState<TrayFolds>(() => trayFolds(nav.tenant, who));
  const fold = (t: Tray) => {
    const next: TrayFolds = { ...folds, [t]: !folds[t] };
    setFolds(next);
    keepTrayFolds(nav.tenant, who, next);
  };
  const [sort, setSort] = useState<VisitSort>({ key: 'time', dir: 'asc' });
  // the hold list past the board's oldest 100: every paused visit (dispatch.paused), re-read with the board
  const [showAllPaused, setShowAllPaused] = useState(false);
  const [allPaused, setAllPaused] = useState<BoardVisit[] | null>(null);
  useEffect(() => {
    if (!showAllPaused || !board) return;
    let live = true;
    client
      .call<{ visits: BoardVisit[]; total: number }>('dispatch.paused', {})
      .then((r) => live && setAllPaused(r.visits))
      .catch(() => {
        if (!live) return;
        setShowAllPaused(false);
        setToast('Couldn’t get every paused visit. Try again.');
      });
    return () => {
      live = false;
    };
  }, [showAllPaused, board]);
  // Tap-to-assign: the waiting visit picked in the tray; the next lane tapped (or Enter on it) gets it.
  const [pick, setPick] = useState<string | null>(null);
  useEffect(() => {
    if (mirroring) setPick(null); // the company began mirroring: a picked visit is put down
  }, [mirroring]);
  // QA-271: one polite line per change someone else makes; your own actions say so in the toast.
  const [said, setSaid] = useState('');
  const last = useRef<Board | null>(null);
  const mine = useRef(new Map<string, number>());
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<Phase | null>(null);
  const [attempt, setAttempt] = useState(0); // Try again starts the ladder over
  const shown = useRef(false); // the day's board is on screen: a failed re-read only toasts
  const [cache] = useState(() => cachedLanes(nav.tenant));

  const load = useCallback(async () => {
    try {
      const b = await client.call<Board>('dispatch.board', { date });
      const prev = last.current;
      if (prev) {
        const now = Date.now();
        const skip = new Set([...mine.current].filter(([, at]) => now - at < 8000).map(([id]) => id));
        const label = (st: string) => STATUS_LABEL[st] ?? st;
        const changes = boardChanges(prev, b, b.timeZone, label, skip);
        if (changes.length) setSaid(sentences(...changes));
        // the open visit changed by someone else: said in its panel, and an Undo of yours on it is gone
        // with a word, never quietly (QA-847)
        const open = selectedRef.current;
        const news = open ? othersChangeTo(open, prev, b, b.timeZone, label, skip) : null;
        const undid = undoIdRef.current;
        const lost = undid ? othersChangeTo(undid, prev, b, b.timeZone, label, skip) : null;
        if (lost) undoDropRef.current();
        if (news || lost)
          setToast(
            sentences(
              news ?? lost!,
              ...(lost ? ['Your Undo is gone: someone else changed it after you did'] : []),
            ),
          );
      }
      last.current = b;
      setBoard(b);
      keepLanes(nav.tenant, b);
    } catch (e) {
      if (shown.current) setToast(e instanceof RpcError ? e.message : 'Could not load the board.');
      else setPhase('error');
    }
  }, [date, nav.tenant]);

  // L-lanes: while the day's board isn't here, the lanes (from the last visit), the hours and the now
  // line show at once; placeholders after 300 ms, the line at 3 s, the error at 10 s. A loaded board
  // is never blanked: live re-reads and actions keep it on screen.
  const loaded = board !== null && board.date === date;
  shown.current = loaded;
  useEffect(() => {
    if (loaded) {
      setPhase(null);
      return;
    }
    setPhase('wait');
    const ts = [
      setTimeout(() => setPhase((p) => (p === 'wait' ? 'ph' : p)), 300),
      setTimeout(() => setPhase((p) => (p === 'error' ? p : 'slow')), 3000),
      setTimeout(() => setPhase('error'), 10_000),
    ];
    return () => ts.forEach(clearTimeout);
  }, [loaded, date, attempt]);

  useEffect(() => {
    void load();
  }, [load, attempt]);

  // Live updates: ids of what changed arrive over the event stream; re-read, debounced.
  useEffect(() => {
    // Through liveStream: it reconnects (QA-058) and stays quiet for a role without the stream (QA-397).
    const es = liveStream();
    let timer: ReturnType<typeof setTimeout> | undefined;
    es.addEventListener('change', (e) => {
      const { type } = JSON.parse((e as MessageEvent<string>).data) as { type: string };
      if (!BOARD_EVENTS.test(type)) return;
      clearTimeout(timer);
      timer = setTimeout(() => void load(), 250);
    });
    return () => {
      clearTimeout(timer);
      es.close();
    };
  }, [load]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);
  useEffect(() => {
    if (!said) return;
    const t = setTimeout(() => setSaid(''), 8000); // clear, so the same words can be said again
    return () => clearTimeout(t);
  }, [said]);
  // Escape puts a picked visit down; so does changing the day. Caught first, and only that: the open
  // visit's panel (which closes on Escape) stays open.
  useEffect(() => {
    if (!pick) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setPick(null);
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [pick]);
  useEffect(() => setPick(null), [date]);

  // ── Blueprint: the tech menu's reads and what it does to the board (pages/board-tech-menu.md) ──
  // The team's day so far (time.teamDay): read with every board read, so the lanes' problem lines and
  // an open menu's card move with the stream; Try again in the card reads it again.
  const [teamDay, setTeamDay] = useState<TeamDay | null>(null);
  const [teamDayFailed, setTeamDayFailed] = useState(false);
  const [teamDayTry, setTeamDayTry] = useState(0);
  // the quick edit of a technician's day, opened from their menu (Fix Fri Oct 23…, Edit hours…)
  const [quickEdit, setQuickEdit] = useState<QuickEditOpen | null>(null);
  const online = useOnline();
  // the technician whose menu is open, and where it opened
  const [menu, setMenu] = useState<{ techId: string; at: MenuAt } | null>(null);
  useEffect(() => {
    if (!bp || !board) return;
    let live = true;
    client
      .call<TeamDay>('time.teamDay', {})
      .then((r) => {
        if (!live) return;
        setTeamDay(r);
        setTeamDayFailed(false);
      })
      .catch(() => live && setTeamDayFailed(true));
    return () => {
      live = false;
    };
  }, [bp, board, teamDayTry, menu?.techId]);
  // their phones, for Call and Text (staff.list, for those who may read the team)
  const [phones, setPhones] = useState<ReadonlyMap<string, string>>(new Map());
  const readsStaff = (nav.permissions ?? []).includes('staff.view');
  useEffect(() => {
    if (!bp || !readsStaff) return;
    let live = true;
    client
      .call<{ staff: { id: string; phone: string | null }[] }>('staff.list', { techniciansOnly: true })
      .then(
        (r) =>
          live && setPhones(new Map(r.staff.flatMap((x) => (x.phone ? [[x.id, x.phone] as const] : [])))),
      )
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [bp, readsStaff]);
  // On the board: show only one technician, or hide some for the day (a new day shows everyone again)
  const [onlyTech, setOnlyTech] = useState<string | null>(null);
  const [hiddenTechs, setHiddenTechs] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => setHiddenTechs(new Set()), [date]);
  const shownTech = (id: string) => (!onlyTech || id === onlyTech) && !hiddenTechs.has(id);
  // Show on map: the strip under the board, with only their route
  const [mapTech, setMapTech] = useState<string | null>(null);
  const mapEl = useRef<HTMLDivElement | null>(null);
  // the status bar under the board: a day's state, or a list (BL-127)
  const [boardTab, setBoardTab] = useState<BoardTab>('all');

  const tz = board?.timeZone ?? cache.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const allVisits = useMemo(() => {
    const m = new Map<string, BoardVisit>();
    for (const t of board?.technicians ?? []) for (const v of t.visits) m.set(v.appointmentId, v);
    for (const v of board?.unassigned ?? []) m.set(v.appointmentId, v);
    return m;
  }, [board]);
  const techName = (id: string) => board?.technicians.find((t) => t.id === id)?.name ?? 'A technician';

  /** Runs an action, then re-reads the board; a stale edit is refused and the board reloaded (F-53). */
  async function act(
    action: string,
    input: unknown,
    // the toast; from the answer when it has words of its own (a move outside its window, BL-014's hours)
    after?: string | null | ((r: never) => string),
  ): Promise<boolean> {
    const id = (input as { appointmentId?: string }).appointmentId;
    if (id) mine.current.set(id, Date.now());
    setBusy(true);
    try {
      const r = await client.call<never>(action, input);
      if (id) mine.current.set(id, Date.now()); // the re-reads that follow don't say it again
      await load();
      const said = typeof after === 'function' ? after(r) : after;
      if (said) setToast(said);
      return true;
    } catch (e) {
      // a visit someone else changed: the board already shows their change (QA-767)
      setToast(refusalWords(e));
      if (e instanceof RpcError && e.code === 'conflict') await load();
      return false;
    } finally {
      setBusy(false);
    }
  }

  /**
   * The Move panel's move (QA-255), sent with the version the panel opened on, so a change someone
   * made since is refused rather than overwritten (QA-693). Null when it moved; the refusal otherwise,
   * for the panel to say (with "Get the latest" when it was someone else's change).
   */
  async function moveFromPanel(input: {
    appointmentId: string;
    start: string;
    end: string;
    arrivalWindow: { start: string; end: string };
  }): Promise<unknown> {
    const was = allVisits.get(input.appointmentId);
    mine.current.set(input.appointmentId, Date.now());
    setBusy(true);
    try {
      type Moved = Parameters<typeof movedWords>[0] & { rowVersion: number };
      const r = await client.call<Moved>('appointments.reschedule', input);
      mine.current.set(input.appointmentId, Date.now()); // the re-reads that follow don't say it again
      await load();
      if (!was) {
        setToast(movedWords(r));
        return null;
      }
      // where it went, the day too, with the same Undo a drop's toast offers (QA-843)
      const text = movedToLine(was, input, tz, r);
      const { rowVersion: _sent, ...to } = input as typeof input & { rowVersion?: number };
      setUndo({
        was,
        plan: { reschedule: to, assign: null, retime: null },
        rowVersion: r.rowVersion,
        text,
      });
      setSaid(text);
      return null;
    } catch (e) {
      if (e instanceof RpcError && e.code === 'conflict') await load();
      return e;
    } finally {
      setBusy(false);
    }
  }

  /**
   * The panel's Change times (BL-158): a started or finished visit's new times, sent with the version
   * the panel opened on (QA-693). Null when they changed; the refusal otherwise, for the panel to say.
   */
  async function retimeFromPanel(
    v: BoardVisit,
    input: { start: string; end: string; confirm?: true; rowVersion: number },
  ): Promise<unknown> {
    mine.current.set(v.appointmentId, Date.now());
    setBusy(true);
    try {
      const r = await client.call<{ rowVersion: number }>('appointments.retime', {
        appointmentId: v.appointmentId,
        ...input,
      });
      mine.current.set(v.appointmentId, Date.now());
      await load();
      // the same Undo a drop's toast offers (QA-766), "Change them anyway" too: back as it was, confirmed
      const text = retimedLine(v, input, tz);
      const { rowVersion: _sent, ...to } = input;
      setUndo({ was: v, plan: retimePlan(v, to), rowVersion: r.rowVersion, text });
      setSaid(text);
      return null;
    } catch (e) {
      if (e instanceof RpcError && e.code === 'conflict') await load();
      return e;
    } finally {
      setBusy(false);
    }
  }

  // BL-070: a visit for a tech who's out goes next instead. The offer comes from the board (a busy
  // tech), or from Dispatch's refusal when the board was behind; queue_full asks to replace the next.
  const [offer, setOffer] = useState<{
    appointmentId: string;
    staffId: string;
    replace: string | null;
  } | null>(null);
  async function dispatchOrOffer(v: BoardVisit) {
    const id = v.appointmentId;
    mine.current.set(id, Date.now());
    setBusy(true);
    try {
      await client.call('appointments.dispatch', { appointmentId: id });
      await load();
    } catch (e) {
      const b = e instanceof RpcError && e.code === 'conflict' ? busyFromRefusal(e.detail) : null;
      if (b) {
        setOffer({ appointmentId: id, staffId: b.staffId, replace: null });
        await load();
      } else setToast(e instanceof RpcError ? e.message : 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  }
  async function sendNext(v: BoardVisit, staffId: string, replace: boolean) {
    mine.current.set(v.appointmentId, Date.now());
    setBusy(true);
    try {
      await client.call('appointments.queueNext', queueInput(v.appointmentId, staffId, replace));
      setOffer(null);
      await load();
      setToast(`${v.customerName}’s visit goes out next for ${techName(staffId)}, when they finish.`);
    } catch (e) {
      const why = e instanceof RpcError && e.code === 'conflict' ? queueRefusal(e.detail) : null;
      if (why === 'queue_full')
        setOffer({ appointmentId: v.appointmentId, staffId, replace: (e as RpcError).message });
      else {
        setOffer(null);
        setToast(e instanceof RpcError ? e.message : 'That did not work. Try again.');
        if (why === 'tech_free') await load();
      }
    } finally {
      setBusy(false);
    }
  }

  /**
   * Assign a visit; warns when an added technician already has a visit then (QA-256). What it did is
   * said on the board with Undo, as a drop's is (QA-842), never only to a screen reader.
   */
  async function assign(v: BoardVisit, lead: string | null, helpers: readonly string[]): Promise<boolean> {
    const input = assignInput(v, lead, helpers);
    const added = input.techIds.filter((t) => !v.techIds.includes(t));
    const warn = board ? overlapWarning(board, v, added) : null;
    mine.current.set(v.appointmentId, Date.now());
    setBusy(true);
    // drawn where it goes while it's sent, and said as waiting while offline (QA-853)
    if (localDateOf(v.start, tz) === date)
      setLanding({
        visitId: v.appointmentId,
        place: {
          techId: lead ?? helpers[0] ?? null,
          start: minuteOf(v.start, date, tz),
          end: minuteOf(v.end, date, tz),
        },
      });
    sendingFor.current = v.appointmentId;
    try {
      const r = await client.call<{ rowVersion: number }>('appointments.assign', input);
      mine.current.set(v.appointmentId, Date.now()); // the re-reads that follow don't say it again
      await load();
      const text = sentences(givenLine(v, input.techIds.map(techName), tz), warn);
      setUndo({
        was: v,
        plan: {
          reschedule: null,
          assign: { appointmentId: v.appointmentId, techIds: input.techIds },
          retime: null,
        },
        rowVersion: r.rowVersion,
        text,
      });
      setSaid(text);
      setDragSay('');
      return true;
    } catch (e) {
      // a visit someone else changed: the board already shows their change (QA-767)
      setToast(refusalWords(e));
      if (e instanceof RpcError && e.code === 'conflict') await load();
      return false;
    } finally {
      setBusy(false);
      setLanding(null);
      sendingFor.current = null;
    }
  }

  // Only a visit still waiting can be picked: one someone else has placed meanwhile is put down.
  const picked = pick ? board?.unassigned.find((v) => v.appointmentId === pick) : undefined;
  /**
   * Tap-to-assign: give the picked waiting visit to this lane's technician. It's a drop on that lane at
   * its own time (QA-851): the same check first, then saved with Undo, or Check the move opens on it.
   */
  function place(techId: string) {
    const v = picked;
    setPick(null);
    if (!v || !dispatching || !board) return;
    // its last give is still out (offline, a slow connection): a second isn't queued behind it (QA-853)
    if (landing?.visitId === v.appointmentId || sendingFor.current === v.appointmentId)
      return setToast(STILL_SENDING);
    const r = placeResult(v, techId);
    if (r.kind === 'refuse') return setToast(r.message);
    if (r.kind !== 'assign') return;
    const { place: to, outcome: o } = laneGive({
      board,
      visit: v,
      techId,
      date,
      now: new Date().toISOString(),
      timeZone: tz,
    });
    if (o.kind === 'refuse') {
      setToast(o.reason ?? 'It can’t go there.');
      setDragSay(o.reason ?? 'It can’t go there.');
      return;
    }
    if (o.kind === 'clean' || o.kind === 'none') return void moveVisit(v, null, to);
    setProposal({
      visitId: v.appointmentId,
      fromTechId: null,
      place: to,
      outcome: o,
      rowVersion: v.rowVersion,
    });
    setSelected(v.appointmentId);
    setDragSay(`${o.head}. The move panel is open to check it.`);
  }

  const isToday = date === localToday(tz);
  const today = localToday(tz);
  // QA-764: the company's day turning over (midnight in its time zone, or the zone itself read): a board
  // left open on today moves to the new today; one on another day stays. Checked every 30 s, and as
  // the window comes back to view.
  const dayWas = useRef(today);
  const [, setDayTurn] = useState(0);
  useEffect(() => {
    const check = () => {
      const now = localToday(tz);
      const was = dayWas.current;
      if (now === was) return;
      dayWas.current = now;
      setDate((d) => rolledDay(d, was, now));
      setDayTurn((n) => n + 1);
    };
    check();
    const t = setInterval(check, 30_000);
    const seen = () => document.visibilityState === 'visible' && check();
    document.addEventListener('visibilitychange', seen);
    window.addEventListener('focus', check);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', seen);
      window.removeEventListener('focus', check);
    };
  }, [tz]);
  // Today's board redraws each minute: the now line moves, and a visit turns late on its own (BL-062).
  const [, setMinute] = useState(0);
  useEffect(() => {
    if (!isToday) return;
    const t = setInterval(() => setMinute((m) => m + 1), 60_000);
    return () => clearInterval(t);
  }, [isToday]);
  const nowIso = new Date(Math.floor(Date.now() / 60_000) * 60_000).toISOString(); // whole minutes: "10:05"
  // BL-062: late (nobody there N minutes after the window) and running long, today only
  const lateAfter = board?.lateAfterMinutes;
  const overdueOf = (v: BoardVisit) => (isToday && loaded ? overdue(v, nowIso, lateAfter) : null);
  const looks = isToday && loaded && board ? needsALook(board, nowIso) : [];
  const stAsOf = board ? serviceTitanAsOfText(board, tz) : null;
  // The day runs to 7 p.m., or to midnight for an after-hours visit or an evening's "now" (ux #11).
  const endHour = isToday && localHours(nowIso, tz) >= DAY_END_HOUR ? 24 : dayEndHour(allVisits.values(), tz);
  const nowLeft = isToday
    ? ((localHours(nowIso, tz) - DAY_START_HOUR) / (endHour - DAY_START_HOUR)) * 100
    : null;
  const nowClock = clock(nowIso, tz);

  // On a phone the lanes scroll sideways: today's opens on "now", once per day shown (ux #11), so a
  // live re-read never moves what the dispatcher has scrolled to.
  const wrapEl = useRef<HTMLElement | null>(null);
  const lanesWrap = useCallback((el: HTMLDivElement | null) => {
    wrapEl.current = el;
    return scrollFocus(el);
  }, []);
  // Blueprint: the Board scrolls itself (.fl-board), so that is what scrolls to now and along a drag
  const bpWrap = useCallback((el: HTMLDivElement | null) => {
    wrapEl.current = el?.querySelector<HTMLElement>('.fl-board') ?? null;
  }, []);
  // the names column, whose width the sideways scroll keeps clear of
  const NAMES = bp ? '.fl-lane__head' : '.lane-row .who';
  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    const el = wrapEl.current;
    if (!loaded || !el || scrolledFor.current === date) return;
    scrolledFor.current = date;
    if (el.scrollWidth <= el.clientWidth) return;
    // today: to now; another day: to its first visit, or the morning when it has none (not today's scroll)
    const to = isToday
      ? el.querySelector<HTMLElement>(bp ? '.fl-now' : '.nowmark')
      : el.querySelector<HTMLElement>(bp ? '.fl-visit' : '.lane-job');
    if (!to) {
      el.scrollLeft = 0;
      return;
    }
    const x = to.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft;
    // a third of the way along what shows beside the names, so the hour before now shows too
    const names = el.querySelector<HTMLElement>(NAMES)?.offsetWidth ?? 0;
    el.scrollLeft = Math.max(0, x - names - (el.clientWidth - names) / 3);
  });
  // the visit the board was opened on, in view once it's drawn on its day (QA-768)
  useEffect(() => {
    const id = scrollToVisit.current;
    if (!id || !loaded || hidden) return;
    const block = document.querySelector<HTMLElement>(`[data-drag-visit="${CSS.escape(id)}"]`);
    if (!block) return;
    scrollToVisit.current = null;
    block.scrollIntoView({ block: 'center', inline: 'center' });
  });
  // BL-128: a job opens as its one page over the board, which stays mounted under it: the day, filters
  // and open visit never leave, and where the screen and the lanes were scrolled comes back
  const keptScroll = useRef<{ y: number; x: number } | null>(null);
  const openTheJob = (jobId: string, visitId?: string) => {
    keptScroll.current = { y: window.scrollY, x: wrapEl.current?.scrollLeft ?? 0 };
    openJobPage(jobId, window, visitId);
  };
  useEffect(() => {
    if (hidden) return void window.scrollTo(0, 0); // the page opens at its top
    const kept = keptScroll.current;
    if (!kept) return;
    keptScroll.current = null;
    if (wrapEl.current) wrapEl.current.scrollLeft = kept.x;
    window.scrollTo(0, kept.y);
  }, [hidden]);
  // ── BL-125: the lanes in divisions ──
  // With divisions set, the lanes are grouped by them in the company's order, "Everyone else" last; a
  // group folds to a thin line a technician, as this person last left it on this browser. A
  // dispatcher's tap on a name opens "Move for today"; owners and managers set the divisions from the
  // link under the board.
  const divisions = (loaded && board.divisions) || [];
  const penIndex = new Map((board?.technicians ?? []).map((t, i) => [t.id, i] as const));
  const [folded, setFolded] = useState<Set<string>>(() => groupFolds(nav.tenant, who));
  const foldGroup = (key: string) =>
    setFolded((f) => {
      const next = toggled(f, key);
      keepGroupFolds(nav.tenant, who, next);
      return next;
    });
  // the lanes grouped by division, or not: the company's way (dispatch.openGrouped) unless this person
  // chose otherwise on this browser; in Blueprint, Group's menu and arranging on the board (concept A)
  const grouping = useBoardGrouping({
    tenant: nav.tenant,
    who,
    permissions: nav.permissions ?? [],
    canDispatch,
    board: loaded ? board : null,
    date,
    isToday,
    dayText: (d) => dateText(d, 'UTC', { year: false, weekday: 'long' }),
    reload: () => void load(),
    say: setSaid,
    folded,
    setFolded: (next) => {
      setFolded(next);
      keepGroupFolds(nav.tenant, who, next);
    },
  });
  const grouped = grouping.grouped;
  const groups = loaded ? (bp ? grouping.groups : grouped ? laneGroups(board) : null) : null;
  const [moving, setMoving] = useState<string | null>(null); // the technician whose move sheet is open
  const [settingDivisions, setSettingDivisions] = useState(false);
  // which units are divisions is Flowology's own setting, so it changes while mirroring too
  // businessUnits.setDivisions (org.edit) and each technician's home division (staff.edit)
  const canSetDivisions = ['org.edit', 'staff.edit'].every((p) => (nav.permissions ?? []).includes(p));
  const dayText = (d: string) => dateText(d, 'UTC', { year: false, weekday: 'long' });
  /** A group's head: its name and count, the button that folds it. */
  const groupHead = (g: LaneGroup) => (
    <div className="lane-row div-head" key={`head:${g.key}`}>
      <button
        type="button"
        className="btn quiet div-fold"
        aria-expanded={!folded.has(g.key)}
        aria-label={groupLabel(g)}
        onClick={() => foldGroup(g.key)}
      >
        <span aria-hidden="true">{folded.has(g.key) ? '▸' : '▾'}</span> {g.name}{' '}
        <span className="muted">({g.techs.length})</span>
      </button>
    </div>
  );
  /** A folded group: a thin line a technician, a mark for each visit, by its time. */
  const miniLines = (g: LaneGroup) => (
    <div className="lane-row div-mini" key={`mini:${g.key}`} aria-hidden="true">
      <div className="who" />
      <div className="lane">
        {g.techs.map((t) => (
          <span key={t.id} className="div-mini-line" title={t.name}>
            {t.visits.map((v) => {
              const { left, width } = placement(v, tz, endHour);
              return <i key={v.appointmentId} style={{ left: `${left}%`, width: `${width}%` }} />;
            })}
          </span>
        ))}
      </div>
    </div>
  );

  // ── BL-124: dragging on the board ──
  // A press on a visit becomes a drag after a few pixels (a mouse or a pen), or after holding still
  // (a finger: a swipe first scrolls the board instead). While it's dragged, the visit is faded where
  // it was and outlined where it would land, and a note by the pointer says what letting go does. The
  // keyboard drives the same drag: the arrows on a focused visit, Enter to let go, Escape to put it back.
  const slot = board?.slotMinutes ?? DEFAULT_SLOT_MINUTES;
  const bounds = { from: DAY_START_HOUR * 60, to: endHour * 60 };
  /** The lanes the arrow keys move between, top to bottom (No one yet isn't one). */
  const laneOrder = loaded ? laneOrderOf(groups, board.technicians, folded).filter(shownTech) : [];
  type Drag = {
    visitId: string;
    fromTechId: string | null;
    edge: DragEdge;
    how: DragHow;
    /** where it was picked up, as minutes of the board's day */
    from: { start: number; end: number };
    place: DragPlace;
    /** picked up from the side tray: it keeps its own time, only the technician changes */
    tray: boolean;
    /** the visit's version when picked up: changed meanwhile (another screen), the move is off (QA-706 3) */
    rowVersion: number;
  };
  const [drag, setDragState] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const setDrag = (d: Drag | null) => {
    dragRef.current = d;
    setDragState(d);
  };
  // a press that may become a drag
  const press = useRef<{
    el: HTMLElement;
    visitId: string;
    fromTechId: string | null;
    edge: DragEdge;
    x: number;
    y: number;
    type: string;
    id: number;
    grab: number;
    tray: boolean;
    held: boolean;
    timer: ReturnType<typeof setTimeout> | null;
  } | null>(null);
  const holdTouch = useRef(false); // a finger has picked a visit up: the page mustn't scroll under it
  const pointAt = useRef<{ x: number; y: number } | null>(null);
  const noteEl = useRef<HTMLDivElement | null>(null);
  // a drop that needs a look: drawn where it would go, the Move panel open on it
  const [proposal, setProposal] = useState<{
    visitId: string;
    fromTechId: string | null;
    place: DragPlace;
    outcome: Outcome;
    /** from the panel's Technician list (QA-706 1): Move it saves that choice, lead and helpers as picked */
    picked?: { lead: string; helpers: string[] };
    /**
     * the visit's version the check was made on (QA-846): Move it is sent with it, so a move someone
     * made meanwhile is refused, not overwritten, and the check closes saying so as soon as it shows
     */
    rowVersion: number;
    /** its moves' idempotency key (QA-848): Try again after no answer is the same move, done once */
    key?: string;
  } | null>(null);
  // a clean drop being saved: drawn where it lands until the board says so
  const [landing, setLanding] = useState<{ visitId: string; place: DragPlace } | null>(null);
  // a clean drop's Undo, for UNDO_MS
  const [undo, setUndo] = useState<{
    was: BoardVisit;
    plan: MovePlan;
    rowVersion: number;
    text: string;
  } | null>(null);
  useEffect(() => {
    if (!undo) return;
    const t = setTimeout(() => setUndo(null), UNDO_MS);
    return () => clearTimeout(t);
  }, [undo]);
  undoIdRef.current = undo?.was.appointmentId ?? null;
  undoDropRef.current = () => setUndo(null);
  const [dragSay, setDragSay] = useState(''); // the keyboard drag's note, read out
  useEffect(() => {
    // a new day, or the company began mirroring: nothing stays picked up or waiting for its check
    setDrag(null);
    setProposal(null);
    setUndo(null);
  }, [date, mirroring]);
  useEffect(() => {
    if (proposal && selected !== proposal.visitId) setProposal(null);
  }, [selected, proposal]);
  // Someone else moved the visit while its check was open (another screen, a live re-read): the check
  // was worked out from where it was, so it closes and says where it is now (QA-846)
  const checkedOn = useRef<BoardVisit | null>(null);
  const sendingFor = useRef<string | null>(null);
  // a move that got no answer (QA-848): it may yet land, and then the re-read shows it as it was tried
  const unanswered = useRef<{ visitId: string; place: DragPlace } | null>(null);
  useEffect(() => {
    if (!proposal) return void (checkedOn.current = null);
    const v = allVisits.get(proposal.visitId);
    if (!checkedOn.current || checkedOn.current.appointmentId !== proposal.visitId)
      checkedOn.current = v ?? null;
    // its own Move it, being saved, re-reads it too: that isn't someone else's
    if (!v || v.rowVersion === proposal.rowVersion || sendingFor.current === proposal.visitId) return;
    const was = checkedOn.current;
    setProposal(null);
    const tried = unanswered.current;
    unanswered.current = null;
    const landed =
      tried?.visitId === v.appointmentId &&
      v.start === instantOfMinute(date, tried.place.start, tz) &&
      v.end === instantOfMinute(date, tried.place.end, tz) &&
      (!tried.place.techId || v.techIds.includes(tried.place.techId));
    if (landed) {
      // the move that got no answer went in after all: said as its own, not someone else's
      const words = sentences('It went through', `${v.customerName} is ${windowText(v, tz)} now`);
      setToast(words);
      setDragSay(words);
      return;
    }
    const words = changedUnderCheck(was ?? v, v, tz, techName);
    setToast(words);
    setDragSay(words);
  }, [allVisits, proposal]);

  const outcomeOf = (d: { visitId: string; fromTechId: string | null; place: DragPlace }): Outcome | null => {
    const v = allVisits.get(d.visitId);
    if (!v || !board) return null;
    return classifyMove({
      board,
      visit: v,
      fromTechId: d.fromTechId,
      to: d.place,
      date,
      now: new Date().toISOString(),
      timeZone: tz,
    });
  };
  const dragOutcome = drag ? outcomeOf(drag) : null;
  // Someone changed the visit while it's held (another screen, a live re-read): the move is off, and
  // it says so, rather than Enter doing nothing (QA-706 3). The keyboard's focus goes back to the visit.
  useEffect(() => {
    const d = dragRef.current;
    if (!d) return;
    const v = allVisits.get(d.visitId);
    if (v && v.rowVersion === d.rowVersion) return;
    setDrag(null);
    const words = sentences(
      `${v?.customerName ?? 'That visit'} changed on another screen, so it wasn’t moved`,
      v ? 'Pick it up again to move it' : null,
    );
    setToast(words);
    setDragSay(words);
    if (d.how === 'key' && v)
      requestAnimationFrame(() =>
        document.querySelector<HTMLElement>(`[data-drag-visit="${d.visitId}"]`)?.focus(),
      );
  }, [allVisits]);
  useEffect(() => {
    if (drag?.how === 'key' && dragOutcome) setDragSay(noteText(dragOutcome, 'key'));
  }, [drag?.how, drag?.place.techId, drag?.place.start, drag?.place.end]);

  /**
   * The panel's Technician list (QA-706 1): a new lead is checked as a drop on their lane is, by the
   * one check (classifyMove: overlaps, hours, the drive, licences). Clean, it's saved; needing a look,
   * the check opens with it; refused, it says why. Helpers, and a visit on another day, as before.
   */
  function assignFromPanel(v: BoardVisit, lead: string | null, helpers: string[]) {
    const place: DragPlace | null =
      lead && lead !== v.leadTechId && !v.techIds.includes(lead) && localDateOf(v.start, tz) === date
        ? { techId: lead, start: minuteOf(v.start, date, tz), end: minuteOf(v.end, date, tz) }
        : null;
    const o = place ? outcomeOf({ visitId: v.appointmentId, fromTechId: v.leadTechId, place }) : null;
    if (!o || !place || o.kind === 'clean' || o.kind === 'none') return void assign(v, lead, helpers);
    if (o.kind === 'refuse') {
      setToast(o.reason ?? 'It can’t go there.');
      return;
    }
    setProposal({
      visitId: v.appointmentId,
      fromTechId: v.leadTechId,
      place,
      outcome: o,
      picked: { lead: lead!, helpers },
      rowVersion: v.rowVersion,
    });
    setDragSay(`${o.head}. The move panel is open to check it.`);
  }

  /** Sends a move: its time, then who; a clean drop's toast offers Undo. True when it all went. */
  async function moveVisit(
    v: BoardVisit,
    fromTechId: string | null,
    place: DragPlace,
    window?: { start: string; end: string } | null,
    /** the version the move was checked on (QA-846): someone else's change since is refused */
    checkedVersion?: number,
    /** its idempotency key (QA-848): Try again after no answer sends the same, so it's done once */
    key: string = newKey(),
  ): Promise<MoveResult> {
    const to = {
      techId: place.techId,
      start: instantOfMinute(date, place.start, tz),
      end: instantOfMinute(date, place.end, tz),
    };
    const plan = movePlan(v, fromTechId, to, window, timeRule(v, new Date().toISOString(), tz));
    const done: MovePlan = { reschedule: null, assign: null, retime: null };
    let rowVersion = checkedVersion ?? v.rowVersion;
    type Moved = { rowVersion: number; outsideWindow?: string | null; hoursConflicts?: { text: string }[] };
    let out: Moved | null = null;
    // each step answered in time, or let go (QA-848): a stalled connection never leaves it hanging
    const within = (step: string) => ({ idempotencyKey: `${key}:${step}`, answerWithinMs: SAVE_WITHIN_MS });
    mine.current.set(v.appointmentId, Date.now());
    setBusy(true);
    setLanding({ visitId: v.appointmentId, place });
    sendingFor.current = v.appointmentId;
    try {
      // a started or finished visit: its times only (BL-158)
      if (plan.retime) {
        out = await client.call<Moved>(
          'appointments.retime',
          { ...plan.retime, rowVersion },
          within('retime'),
        );
        rowVersion = out.rowVersion;
        done.retime = plan.retime;
      }
      if (plan.reschedule) {
        out = await client.call<Moved>(
          'appointments.reschedule',
          { ...plan.reschedule, rowVersion },
          within('reschedule'),
        );
        rowVersion = out.rowVersion;
        done.reschedule = plan.reschedule;
      }
      if (plan.assign) {
        out = await client.call<Moved>(
          'appointments.assign',
          { ...plan.assign, rowVersion },
          within('assign'),
        );
        rowVersion = out.rowVersion;
        done.assign = plan.assign;
      }
      mine.current.set(v.appointmentId, Date.now()); // the re-reads that follow don't say it again
      await load();
      const techName2 = place.techId && place.techId !== fromTechId ? techName(place.techId) : null;
      // a waiting visit given out at its own time: who has it now, and when (QA-842)
      const given = !v.techIds.length && plan.assign && !plan.reschedule;
      const text = sentences(
        plan.retime
          ? retimedLine(v, to, tz)
          : given
            ? givenLine(v, plan.assign!.techIds.map(techName), tz)
            : movedLine(v, { techName: techName2, start: to.start, end: to.end }, tz),
        plan.retime ? null : out?.outsideWindow,
        ...(out?.hoursConflicts ?? []).map((c) => c.text),
      );
      setUndo({ was: v, plan: done, rowVersion, text });
      setSaid(text);
      setDragSay(''); // not "The move panel is open" once it has closed
      return 'moved';
    } catch (e) {
      if (e instanceof RpcError && e.code === 'no_answer') {
        // it may have landed: kept as tried, so the board's re-read isn't taken for someone else's
        unanswered.current = { visitId: v.appointmentId, place };
        const words = noAnswerLine(v, tz);
        setToast(words);
        setDragSay(words);
        void load(); // not waited for: the connection that stalled may stall it too
        return 'no-answer';
      }
      const why = refusalWords(e);
      // the time moved but the technician didn't: it says so, and Undo puts the time back
      setToast(done.reschedule ? sentences('The time moved, but the technician didn’t', why) : why);
      if (done.reschedule) setUndo({ was: v, plan: done, rowVersion, text: 'The time moved.' });
      await load();
      return 'refused';
    } finally {
      setBusy(false);
      setLanding(null);
      sendingFor.current = null;
    }
  }
  /** Undo puts the visit back: its technicians, then its time, refused if anyone changed it since. */
  async function undoMove() {
    const u = undo;
    setUndo(null);
    if (!u) return;
    const back = undoPlan(u.was, u.plan);
    let rowVersion = u.rowVersion;
    mine.current.set(u.was.appointmentId, Date.now());
    setBusy(true);
    try {
      if (back.assign)
        rowVersion = (
          await client.call<{ rowVersion: number }>('appointments.assign', { ...back.assign, rowVersion })
        ).rowVersion;
      if (back.reschedule) await client.call('appointments.reschedule', { ...back.reschedule, rowVersion });
      if (back.retime) await client.call('appointments.retime', { ...back.retime, rowVersion });
      mine.current.set(u.was.appointmentId, Date.now());
      await load();
      setToast(`${u.was.customerName}’s visit is back where it was.`);
    } catch (e) {
      setToast(e instanceof RpcError ? refusalWords(e) : 'Couldn’t put it back. Try again.');
      await load();
    } finally {
      setBusy(false);
    }
  }

  /**
   * BL-124: a clean drop moved it at once; Undo puts it back for UNDO_MS. With a visit open it's said
   * in the panel, as the board's other news is, so the panel never covers it (QA-692); with none, at
   * the foot, over the lanes.
   */
  const undoToast = (where: 'in-panel' | 'board-toast') => {
    // offline, a move waits to go: said until it's answered, not saved yet (QA-853)
    const waiting =
      landing && !online
        ? (() => {
            const v = allVisits.get(landing.visitId);
            return v ? waitingToSendLine(v, landing.place, techName) : OFFLINE_WAITING;
          })()
        : null;
    if (bp) {
      const place = where === 'in-panel' ? 'panel' : 'foot';
      if (waiting) return <Toast place={place}>{waiting}</Toast>;
      return (
        undo && (
          <Toast
            place={place}
            actions={
              <FlButton size="touch" isDisabled={busy} onPress={() => void undoMove()}>
                Undo
              </FlButton>
            }
          >
            {undo.text}
          </Toast>
        )
      );
    }
    return waiting ? (
      <div className={`toast ${where}`} role="status">
        {waiting}
      </div>
    ) : (
      undo && (
        <div className={`toast ${where} drag-undo`} role="status">
          <span>{undo.text}</span>
          <button className="btn small soft" disabled={busy} onClick={() => void undoMove()}>
            Undo
          </button>
        </div>
      )
    );
  };

  /** Picks the pressed visit up; a cancelled one says why it stays. */
  const beginDrag = (
    p: { visitId: string; fromTechId: string | null; edge: DragEdge; tray: boolean },
    how: DragHow,
  ): Drag | null => {
    const v = allVisits.get(p.visitId);
    if (!v) return null;
    const why = cantPickUp(v);
    if (why) {
      setToast(why);
      setDragSay(why);
      return null;
    }
    setHover(null);
    setProposal(null);
    setPick(null);
    // picked up where it's drawn: a started visit from when work began (QA-844)
    const span = shownSpan(v);
    const from = { start: minuteOf(span.start, date, tz), end: minuteOf(span.end, date, tz) };
    const d: Drag = { ...p, how, from, place: { techId: p.fromTechId, ...from }, rowVersion: v.rowVersion };
    setDrag(d);
    return d;
  };
  /** Lets go: moves it (clean), opens the Move panel on it (needs a look), or puts it back saying why. */
  const endDrag = (drop: boolean) => {
    const d = dragRef.current;
    const p = press.current;
    if (p?.timer) clearTimeout(p.timer);
    press.current = null;
    holdTouch.current = false;
    if (!d) return;
    setDrag(null);
    const o = outcomeOf(d);
    const v = allVisits.get(d.visitId);
    const refocus = () =>
      d.how === 'key' &&
      requestAnimationFrame(() =>
        document.querySelector<HTMLElement>(`[data-drag-visit="${d.visitId}"]`)?.focus(),
      );
    if (!drop || !o || !v || o.kind === 'none') {
      setDragSay(drop ? '' : 'Put back where it was.');
      return refocus();
    }
    if (o.kind === 'refuse') {
      setToast(o.reason ?? 'It can’t go there.');
      setDragSay(o.reason ?? 'It can’t go there.');
      return refocus();
    }
    if (o.kind === 'clean') return void moveVisit(v, d.fromTechId, d.place).then(refocus);
    setProposal({
      visitId: d.visitId,
      fromTechId: d.fromTechId,
      place: d.place,
      outcome: o,
      rowVersion: d.rowVersion,
    });
    setSelected(d.visitId);
    setDragSay(`${o.head}. The move panel is open to check it.`);
  };

  /** A lane's time track: Riverstone's .lane, Blueprint's BoardLane track. */
  const TRACK = '.lane, .fl-lane__track';
  /** The lane under a point (a technician's id, or 'none' for No one yet and the tray), and its track. */
  const laneAt = (x: number, y: number): { id: string; track: HTMLElement | null } | null => {
    const hit = document
      .elementsFromPoint(x, y)
      .map((n) => (n as HTMLElement).closest?.<HTMLElement>('[data-lane]'))
      .find(Boolean);
    if (!hit) return null;
    return { id: hit.dataset.lane!, track: hit.querySelector<HTMLElement>(TRACK) };
  };
  const placeNote = () => {
    const el = noteEl.current;
    const d = dragRef.current;
    if (!el || !d) return;
    const tw = el.offsetWidth;
    const th = el.offsetHeight;
    // beside the pointer; above a finger, which would cover it; under a keyboard move's outline
    let x = pointAt.current?.x ?? 0;
    let y = pointAt.current?.y ?? 0;
    if (d.how === 'key') {
      const g = document
        .querySelector<HTMLElement>('.drag-ghost, .fl-lane__ghost .fl-visit')
        ?.getBoundingClientRect();
      x = g ? g.left + Math.min(g.width, 160) / 2 : x;
      y = g ? g.bottom : y;
    }
    const left = Math.min(Math.max(8, x - tw / 2), window.innerWidth - tw - 8);
    let top = d.how === 'touch' ? y - th - 56 : y + (d.how === 'key' ? 10 : 22);
    if (top + th > window.innerHeight - 8) top = Math.max(8, y - th - 18);
    if (top < 8) top = y + 30;
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  };
  useEffect(placeNote);

  const dragMove = (x: number, y: number) => {
    const d = dragRef.current;
    const p = press.current;
    if (!d || !p) return;
    pointAt.current = { x, y };
    let place: DragPlace = d.place;
    if (d.edge === 'move') {
      const hit = laneAt(x, y);
      const techId = hit ? (hit.id === 'none' ? null : hit.id) : d.place.techId;
      const track = hit?.track ?? null;
      const t =
        d.tray || !track
          ? d.from
          : dragTo(
              'move',
              d.from,
              minuteAtX(x, track.getBoundingClientRect(), endHour),
              p.grab,
              bounds,
              slot,
            );
      place = { techId, ...t };
    } else {
      const track = p.el.closest<HTMLElement>(TRACK);
      if (track)
        place = {
          techId: d.place.techId,
          ...dragTo(d.edge, d.from, minuteAtX(x, track.getBoundingClientRect(), endHour), 0, bounds, slot),
        };
    }
    if (place.techId !== d.place.techId || place.start !== d.place.start || place.end !== d.place.end)
      setDrag({ ...d, place });
    placeNote();
  };
  // near the lanes' edge, they scroll along with the drag (they scroll sideways at phone width)
  useEffect(() => {
    if (!drag || drag.how === 'key') return;
    let raf = 0;
    const step = () => {
      const el = wrapEl.current;
      const at = pointAt.current;
      if (el && at && el.scrollWidth > el.clientWidth) {
        const r = el.getBoundingClientRect();
        const names = el.querySelector<HTMLElement>(NAMES)?.offsetWidth ?? 0;
        const by = at.x > r.right - 24 ? 8 : at.x < r.left + names + 12 && el.scrollLeft > 0 ? -8 : 0;
        if (by) {
          el.scrollLeft += by;
          dragMove(at.x, at.y);
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [!!drag && drag.how !== 'key']);

  const onPressDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (!dispatching || dragRef.current || e.button > 0) return;
    const target = e.target as HTMLElement;
    const el = target.closest<HTMLElement>('[data-drag-visit]');
    if (!el) return;
    const v = allVisits.get(el.dataset.dragVisit!);
    if (!v) return;
    let edge = (target.closest<HTMLElement>('[data-edge]')?.dataset.edge as DragEdge | undefined) ?? 'move';
    const from = el.dataset.dragFrom;
    // Blueprint's block has no end handles: a press near either end of a lane's visit takes that end
    if (bp && edge === 'move' && from && from !== 'none' && from !== 'tray' && !cantPickUp(v)) {
      const r = el.getBoundingClientRect();
      const grip = Math.min(10, r.width / 4);
      if (e.clientX - r.left <= grip) edge = 'start';
      else if (r.right - e.clientX <= grip) edge = 'end';
    }
    const track = el.closest<HTMLElement>(TRACK);
    const grab = track
      ? minuteAtX(e.clientX, track.getBoundingClientRect(), endHour) - minuteOf(v.start, date, tz)
      : 0;
    const p = {
      el,
      visitId: v.appointmentId,
      fromTechId: from && from !== 'none' && from !== 'tray' ? from : null,
      edge,
      x: e.clientX,
      y: e.clientY,
      type: e.pointerType,
      id: e.pointerId,
      grab,
      tray: from === 'tray',
      held: false,
      timer: null as ReturnType<typeof setTimeout> | null,
    };
    press.current = p;
    pointAt.current = { x: e.clientX, y: e.clientY };
    if (edge !== 'move')
      e.preventDefault(); // an end is held: no text selection, no scroll
    else if (e.pointerType === 'touch')
      p.timer = setTimeout(() => {
        if (press.current !== p) return;
        p.held = true;
        holdTouch.current = true;
        navigator.vibrate?.(12);
        if (beginDrag(p, 'touch')) dragMove(p.x, p.y);
        else press.current = null;
      }, HOLD_MS);
  };
  // the window's own listeners, once: each hands the event to this render's handlers
  const onWindow = useRef({ move: (_e: PointerEvent) => {}, up: (_e: PointerEvent, _drop: boolean) => {} });
  onWindow.current = {
    move: (e) => {
      const p = press.current;
      if (!p || e.pointerId !== p.id) return;
      if (!dragRef.current) {
        const far = Math.hypot(e.clientX - p.x, e.clientY - p.y);
        if (p.type === 'touch' && p.edge === 'move' && !p.held) {
          if (far > TOUCH_SLOP_PX) {
            if (p.timer) clearTimeout(p.timer);
            press.current = null; // a swipe: the board scrolls
          }
          return;
        }
        if (far < MOUSE_SLOP_PX) return;
        if (!beginDrag(p, p.type === 'touch' ? 'touch' : 'mouse')) {
          press.current = null;
          return;
        }
      }
      e.preventDefault();
      dragMove(e.clientX, e.clientY);
    },
    up: (e, drop) => {
      const p = press.current;
      if (!p || e.pointerId !== p.id) return;
      if (p.timer) clearTimeout(p.timer);
      if (!dragRef.current) {
        press.current = null;
        return;
      }
      // the click that follows a drag isn't a tap on the visit
      const eat = (c: MouseEvent) => {
        c.stopPropagation();
        c.preventDefault();
      };
      window.addEventListener('click', eat, { capture: true, once: true });
      setTimeout(() => window.removeEventListener('click', eat, { capture: true }), 350);
      endDrag(drop);
    },
  };
  useEffect(() => {
    const move = (e: PointerEvent) => onWindow.current.move(e);
    const up = (e: PointerEvent) => onWindow.current.up(e, true);
    const cancel = (e: PointerEvent) => onWindow.current.up(e, false);
    const touch = (e: TouchEvent) => {
      if (holdTouch.current) e.preventDefault();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    document.addEventListener('touchmove', touch, { passive: false });
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      document.removeEventListener('touchmove', touch);
    };
  }, []);

  /** The keyboard's drag: arrows on a focused visit move it, Enter lets go, Escape puts it back. */
  const onDragKey = (e: ReactKeyboardEvent<HTMLElement>) => {
    if (!dispatching) return;
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-drag-visit]');
    if (!el || el.dataset.dragFrom === 'tray') return;
    const d = dragRef.current;
    if (d?.how === 'key' && d.visitId === el.dataset.dragVisit) {
      if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        return endDrag(e.key !== 'Escape');
      }
    }
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    e.preventDefault();
    const from = el.dataset.dragFrom;
    const cur =
      d?.how === 'key'
        ? d
        : beginDrag(
            {
              visitId: el.dataset.dragVisit!,
              fromTechId: from && from !== 'none' ? from : null,
              edge: 'move',
              tray: false,
            },
            'key',
          );
    if (!cur) return;
    const next = keyStep(cur.place, e.key, e.shiftKey, laneOrder, bounds, slot);
    if (!next) return;
    // at the board's edge the arrows stop: it says why, rather than nothing (QA-706 5)
    const kv = allVisits.get(cur.visitId);
    const edge = keyEdge(
      cur.place,
      next,
      e.key,
      e.shiftKey,
      kv ? timeRule(kv, new Date().toISOString(), tz) : 'free',
    );
    if (edge) setDragSay(edge);
    else setDrag({ ...cur, place: next });
  };
  const onDragBlur = (e: ReactFocusEvent<HTMLElement>) => {
    const d = dragRef.current;
    if (d?.how === 'key' && (e.target as HTMLElement).dataset?.dragVisit === d.visitId) endDrag(false);
  };

  /** Where a visit shows while it's dragged, waiting for its check, or landing: one outline, in its lane. */
  const shadow = (laneId: string | null): ReactNode => {
    const g = drag
      ? { visitId: drag.visitId, place: drag.place, cls: dragOutcome?.kind ?? 'none' }
      : proposal
        ? { visitId: proposal.visitId, place: proposal.place, cls: 'pending' }
        : landing
          ? { visitId: landing.visitId, place: landing.place, cls: 'landing' }
          : null;
    if (!g || laneId === null || g.place.techId !== laneId) return null;
    const v = allVisits.get(g.visitId);
    if (!v) return null;
    const start = instantOfMinute(date, g.place.start, tz);
    const end = instantOfMinute(date, g.place.end, tz);
    const { left, width } = placement({ start, end }, tz, endHour);
    const t = clock(start, tz);
    return (
      <span
        className={`lane-job drag-ghost ${g.cls}`}
        style={{ left: `${left}%`, width: `${Math.max(width, MIN_PILL_PCT)}%` }}
        aria-hidden="true"
      >
        <span className="tl">
          <span className="t">{t.time}</span>
          <span className="ti">{v.customerName}</span>
        </span>
        <span className="pl">
          <span className="st">
            {g.cls === 'pending' ? 'Moving here' : windowText({ start, end, arrivalWindow: null }, tz)}
          </span>
        </span>
      </span>
    );
  };
  /** A visit's place in the drag: the one picked up, or waiting for its check where it was. */
  const dragClass = (id: string) =>
    drag?.visitId === id ? ' lifting' : proposal?.visitId === id || landing?.visitId === id ? ' was' : '';

  // BL-127: the tabs under the board (Blueprint): each one's count, the open one's list, re-read with it
  const [tab, setTab] = useState<ListTab>('onHold');
  const [lists, setLists] = useState<BoardLists | null>(null);
  const [listsFailed, setListsFailed] = useState(false);
  const [listsMore, setListsMore] = useState(false);
  // the rows the open tab shows, so the board's refresh reads as many again (up to the most a read gives)
  const listShown = useRef<{ tab: ListTab; n: number }>({ tab, n: 0 });
  listShown.current =
    lists?.tab === tab ? { tab, n: lists.visits.length || lists.jobs.length } : { tab, n: 0 };
  useEffect(() => {
    if (!bp || !board) return;
    let live = true;
    client
      .call<BoardLists>('dispatch.lists', { tab, limit: listReadSize(listShown.current.n) })
      .then((r) => {
        if (!live) return;
        setLists(r);
        setListsFailed(false);
      })
      .catch(() => live && setListsFailed(true));
    return () => {
      live = false;
    };
  }, [bp, board, tab]);
  // the open tab's next page, under the rows shown (list caps, BL-110); a tab changed meanwhile drops it
  const moreOfList = () => {
    if (!lists?.next || listsMore) return;
    const had = lists;
    setListsMore(true);
    client
      .call<BoardLists>('dispatch.lists', { tab: had.tab, limit: LIST_PAGE, after: had.next })
      .then((r) => setLists((l) => (l && l.tab === r.tab ? addListPage(l, r) : l)))
      .catch(() => setListsFailed(true))
      .finally(() => setListsMore(false));
  };
  // a paused visit on another day opens from the hold list, though it isn't on this day's board
  const pausedList = (showAllPaused && allPaused) || board?.paused || [];
  const sel = selected
    ? (allVisits.get(selected) ?? pausedList.find((v) => v.appointmentId === selected))
    : undefined;
  const held = loaded && board ? pausedGroups(pausedList) : [];
  // BL-100: the open visit's drawer starts at the board's top, or the screen's once that has scrolled
  // past it, so it never covers the header; Esc closes it when nothing is picked (Esc puts that down)
  const boardEl = useRef<HTMLElement | null>(null);
  const [drawerTop, setDrawerTop] = useState(16);
  const open = !!sel;
  useEffect(() => {
    if (!open) return;
    const at = () =>
      setDrawerTop(Math.max(16, Math.round(boardEl.current?.getBoundingClientRect().top ?? 16)));
    at();
    window.addEventListener('scroll', at, { passive: true });
    window.addEventListener('resize', at);
    return () => {
      window.removeEventListener('scroll', at);
      window.removeEventListener('resize', at);
    };
  }, [open, view]);
  useEffect(() => {
    if (!open || pick || hidden) return;
    const onKey = (e: KeyboardEvent) => {
      // not while a sheet over the board (the job) has Esc for itself
      if (e.key === 'Escape' && !e.defaultPrevented && !document.querySelector('dialog[open]'))
        setSelected(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, pick, hidden]);
  const pausedTotal = Math.max(board?.pausedTotal ?? 0, pausedList.length);
  const theDay = loaded && board ? dayVisits(board) : [];
  // the bar counts what the other filters keep; the table and the lanes follow all of them, the
  // Technician choice too, as the lanes do (QA-845)
  const techKept = (v: BoardVisit) => keepsTech(v, onlyTech, hiddenTechs);
  const forBar = theDay.filter((v) => keeps(v, filter, 'status') && techKept(v));
  const counts = statusCounts(forBar);
  const tableRows = sortedVisits(
    theDay.filter((v) => keeps(v, filter) && techKept(v)),
    sort,
    techName,
  );
  const opts = filterOptions(theDay);
  const filtering = !!(filter.unit || filter.type || filter.tag);
  const goesOut = sel ? laterDay(sel, tz, localToday(tz)) : null;
  // Blueprint: the five figures over the board, and each technician's pen by their place on it
  const figures =
    bp && loaded && board
      ? boardFigures(
          theDay,
          looks.filter((l) => overdueTone(l.overdue) !== 'stale').length,
          board.unassigned.length,
        )
      : null;
  // Riverstone's pill. row: which row of its lane the pill sits in (laneRows); now: the visit the tech
  // is out on right now
  const pill = (v: BoardVisit, fromTechId: string | null, row = 0, now = false, next = false) => {
    const { left, width } = placement(v, tz, endHour);
    const t = clock(v.arrivalWindow?.start ?? v.start, tz);
    const o = overdueOf(v);
    const badge = o ? overdueWords(o, tz).badge : null;
    const needs = (v.licenceGaps ?? []).length > 0; // BL-027: nobody on it holds a licence it needs
    const said = techMessagesWords(v.newFromTechs); // BL-028: the techs wrote, and nobody's read it yet
    const tags = v.tags ?? [];
    // a lane shows its own technician's state on a shared visit (dispatch parity 2)
    const st = laneStatus(v, fromTechId);
    const shown = st === v.status ? v : { ...v, status: st, notStarted: st === 'dispatched' && v.notStarted };
    // BL-124: a visit that hasn't started can be dragged, by its middle or either end
    const movable = dispatching && !cantPickUp(v);
    return (
      <button
        key={`${v.appointmentId}:${fromTechId ?? 'tray'}`}
        className={`lane-job ${st}${keeps(v, filter) ? '' : ' faded'}${now ? ' now' : ''}${next ? ' next' : ''}${o ? ` ${overdueTone(o)}` : ''}${selected === v.appointmentId ? ' selected' : ''}${movable ? ' movable' : ''}${dragClass(v.appointmentId)}`}
        // a stacked pill's row (laneRows): styles.css sets its top from the lane's pad and pitch tokens
        style={
          {
            left: `${left}%`,
            width: `${Math.max(width, MIN_PILL_PCT)}%`,
            ...(row ? { '--row': row } : {}),
          } as CSSProperties
        }
        // the browser's own drag stays off: a visit moves by pointer events (BL-124)
        draggable={false}
        data-drag-visit={v.appointmentId}
        data-drag-from={fromTechId ?? 'none'}
        onPointerDown={onPressDown}
        onKeyDown={onDragKey}
        onBlur={onDragBlur}
        onMouseEnter={(e) => {
          if (!canHover || dragRef.current) return;
          const r = e.currentTarget.getBoundingClientRect();
          setHover({ id: v.appointmentId, left: r.left, top: r.top, bottom: r.bottom });
        }}
        onMouseLeave={() => setHover(null)}
        onClick={() => {
          setHover(null);
          if (!pick) setSelected(v.appointmentId); // while picking, the tap is for the lane
        }}
        // a double-click goes straight to the job
        onDoubleClick={() => {
          if (pick) return;
          setHover(null);
          setSelected(null);
          openTheJob(v.jobId);
        }}
        aria-label={`${t.time} ${t.suffix}, job ${v.jobNumber}, ${v.customerName}, ${visitStatusLabel(shown, STATUS_LABEL)}${now ? ', now' : ''}${next ? ', next' : ''}${badge ? `, ${badge.toLowerCase()}` : ''}${needs ? ', needs a licence' : ''}${said ? `, ${said}` : ''}${tagsLabel(tags)}`}
        {...(movable && fromTechId ? { 'aria-describedby': 'board-drag-how' } : {})}
        title={`${t.time} ${t.suffix} · ${v.customerName} · #${v.jobNumber}`}
      >
        {movable && fromTechId && (
          <>
            <span className="drag-edge start" data-edge="start" aria-hidden="true" />
            <span className="drag-edge end" data-edge="end" aria-hidden="true" />
          </>
        )}
        <span className="tl">
          {now && <span className="now-badge">Now</span>}
          {next && <span className="now-badge next-badge">Next</span>}
          <span className="t">{t.time}</span>
          <span className="ti">{v.customerName}</span>
        </span>
        <span className="pl">
          {o && (
            // on the second line, so a short pill still shows it; "Long" when "Running long" won't fit
            <span
              className={`overdue-badge ${overdueTone(o)}`}
              data-short={o.kind === 'long' && !o.stale ? 'Long' : undefined}
            >
              <span>{badge}</span>
            </span>
          )}
          {needs && <span className="licence-badge">Licence</span>}
          {said && (
            <span className="msg-badge" title={said}>
              ✉ {v.newFromTechs}
            </span>
          )}
          <span className="jn">#{v.jobNumber}</span>
          {tags.length > 0 && (
            // the first chip, then "+2" for the rest; a wide pill shows them all (styles.css), never a
            // wrap. Only the summary gives way to an ellipsis, so the chips always show whole.
            <span className="tag-chips" aria-hidden="true">
              {tags.map((g, i) => (
                <span
                  key={g.id}
                  className={`tag-chip${i ? ' extra' : ''}`}
                  style={g.color ? { background: g.color, color: tagInk(g.color) } : undefined}
                >
                  {g.label}
                </span>
              ))}
              {tags.length > 1 && <span className="tag-chip more">+{tags.length - 1}</span>}
            </span>
          )}
          <span className="sm"> · {plainText(v.summary)}</span>
        </span>
      </button>
    );
  };

  const dayName = new Intl.DateTimeFormat('en-CA', { weekday: 'long', timeZone: 'UTC' }).format(
    new Date(`${date}T12:00:00Z`),
  );
  const what = isToday ? "today's board" : `${dayName}'s board`;
  const blank = <span className="ph-bar" aria-hidden="true" />;

  const dateWords =
    view === 'week'
      ? (() => {
          const [a, b] = weekOf(date);
          const f = (d: string, o: Intl.DateTimeFormatOptions) =>
            new Intl.DateTimeFormat('en-CA', { ...o, timeZone: 'UTC' }).format(new Date(`${d}T12:00:00Z`));
          return `${f(a, { month: 'long', day: 'numeric' })} – ${f(b, a.slice(5, 7) === b.slice(5, 7) ? { day: 'numeric' } : { month: 'long', day: 'numeric' })}`;
        })()
      : new Intl.DateTimeFormat('en-CA', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          timeZone: 'UTC',
        }).format(new Date(`${date}T12:00:00Z`));
  // the toolbar's filters: beside the title in Riverstone; in Blueprint, a line of their own over the board
  const controls = (
    <>
      {/* while ServiceTitan's visits are on it, how fresh they are (history fix 4) */}
      {view === 'day' && loaded && stAsOf && <p className="muted small st-as-of">{stAsOf}</p>}
      {view === 'day' && loaded && theDay.length > 0 && (
        // dispatch parity 5: the day's visits by state; a tap shows only those, on the lanes and in the table
        <div className="pills status-counts" role="group" aria-label="Show visits by state">
          <button className="pill" aria-pressed={!only} onClick={() => setOnly(null)}>
            All {forBar.length}
          </button>
          {counts.map((c) => (
            <button
              key={c.status}
              className="pill"
              aria-pressed={only === c.status}
              onClick={() => setOnly(only === c.status ? null : c.status)}
            >
              {STATUS_LABEL[c.status] ?? c.status} {c.count}
            </button>
          ))}
        </div>
      )}
      {view === 'day' &&
        loaded &&
        (opts.units.length > 1 || opts.types.length > 1 || opts.tags.length > 0) && (
          // dispatch parity 7: what the day's visits offer, each "All" until chosen
          <div className="board-filters" role="group" aria-label="Filter the board">
            {(
              [
                // the job's department, not the technician's division (QA-845)
                ['unit', 'Job’s department', opts.units],
                ['type', 'Job type', opts.types],
                ['tag', 'Tag', opts.tags],
              ] as const
            ).map(
              ([key, label, list]) =>
                list.length > (key === 'tag' ? 0 : 1) && (
                  <label key={key} className="f inline">
                    {label}
                    <select
                      className="field"
                      value={filter[key] ?? ''}
                      onChange={(e) => setFilter({ ...filter, [key]: e.target.value || null })}
                    >
                      <option value="">All</option>
                      {list.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ),
            )}
            {filtering && (
              <button
                className="btn quiet small"
                onClick={() => setFilter({ ...NO_FILTER, status: filter.status })}
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      {canHover && view === 'day' && (
        <div className="seg density" role="group" aria-label="Board density">
          {(['comfortable', 'compact'] as const).map((d) => (
            <button
              key={d}
              className={`btn quiet${density === d ? ' on' : ''}`}
              aria-pressed={density === d}
              onClick={() => {
                setDensity(d);
                keepDensity(nav.tenant, who, d);
              }}
            >
              {d === 'comfortable' ? 'Comfortable' : 'Compact'}
            </button>
          ))}
        </div>
      )}
    </>
  );
  // Blueprint's title block (DESIGN.md §3): the day's board by name, its date and what to do under it
  const canBook = PLACES.some((p) => p.view === 'book' && canSee(p, nav.permissions ?? []));
  // To schedule's link by the day's figures (Amir, 26 Oct), counted as the Schedule place counts it,
  // until the board's job table has a tab for it (BL-158)
  const toSchedule = PLACES.some((p) => p.view === 'tobook' && canSee(p, nav.permissions ?? []))
    ? waitingOn(nav, 'tobook')
    : 0;
  const bpLede = `${dateWords}. ${
    !dispatching
      ? canDispatch
        ? `The techs’ day, updated as they work. ${MIRROR_LINE}`
        : 'The techs’ day, updated as they work. Dispatchers give the visits to the techs.'
      : view === 'week'
        ? 'Tap a day to open its board.'
        : 'Tap a visit to see it, then move it or give it to someone else.'
  }`;

  /** A technician's lane: the name (and, with divisions, its menu), then the day. */
  const laneRow = (tech: BoardTech) => (
    <div className={`lane-row${tech.divisionMove ? ' div-moved' : ''}`} key={tech.id} data-lane={tech.id}>
      {/* the name is part of the row: a tap or a drop on it gives the visit to this tech too, so
        on a phone, where the names stay put over the scrolled lane, a finger on them still lands */}
      <div
        className={`who${picked ? ' pickable' : ''}`}
        {...(picked ? { onClick: () => place(tech.id) } : {})}
      >
        <span className="avatar" style={{ background: tech.color ?? 'var(--brand)' }}>
          {tech.initials}
        </span>
        {/* BL-125: a dispatcher's tap on the name opens the move for the day (not while picking a visit) */}
        {dispatching && divisions.length > 0 && !picked ? (
          <button
            type="button"
            className="name name-menu"
            aria-haspopup="dialog"
            title={`Move ${tech.name} to another division for ${isToday ? 'today' : 'the day'}`}
            onClick={() => setMoving(tech.id)}
          >
            {tech.name}
          </button>
        ) : (
          <span className="name">{tech.name}</span>
        )}
        {usuallyLine(tech, divisions, isToday) && (
          <span className="div-usually">{usuallyLine(tech, divisions, isToday)}</span>
        )}
        {/* out right now: said on today's board only, never on another day's */}
        {isToday && outChip(tech) && (
          <span className="chip brand out" title={outChip(tech)!}>
            {outChip(tech)}
          </span>
        )}
        {leftOpen.some((p) => p.staffId === tech.id) && (
          <span className="chip bad left-open-chip">Clock-out missed</span>
        )}
        {(() => {
          // BL-014: away all day, off, or on non-job time now; then, to a screen reader, the rest
          const { chip, more } = laneAway(
            tech.days?.find((d) => d.date === date),
            isToday,
            tz,
            nowIso,
          );
          return (
            <>
              {chip && (
                <span className="chip away-chip" title={chip}>
                  {chip}
                </span>
              )}
              {more.length > 0 && <span className="sr-only">{more.join('; ')}</span>}
            </>
          );
        })()}
      </div>
      <div
        className={`lane${picked ? ' pickable' : ''}${drag?.edge === 'move' && drag.place.techId === tech.id && drag.place.techId !== drag.fromTechId ? ' drop' : ''}`}
        style={{ '--rows': laneRows(tech.visits, tz, endHour).rows } as CSSProperties}
        aria-label={picked ? `Give ${picked.customerName}’s visit to ${tech.name}` : `${tech.name}'s day`}
        {...(picked
          ? {
              role: 'button',
              tabIndex: 0,
              onClick: () => place(tech.id),
              onKeyDown: (e: ReactKeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  place(tech.id);
                }
              },
            }
          : {})}
      >
        {/* BL-014: the hours they don't work shaded, and what takes them away, under the pills */}
        <div className="lane-bands" aria-hidden="true">
          {laneBands(
            tech.days?.find((d) => d.date === date),
            tz,
            endHour,
            nowIso,
          ).map((b, i) => (
            <span
              key={i}
              className={`band ${b.kind}${b.left === 0 ? ' edge' : ''}`}
              style={{ left: `${b.left}%`, width: `${b.width}%` }}
              title={b.title}
            >
              {b.label}
            </span>
          ))}
        </div>
        {(() => {
          // overlapping visits stack in rows, the lane taller for each: nothing hides
          const lanes = laneRows(tech.visits, tz, endHour);
          return tech.visits.map((v) =>
            pill(
              v,
              tech.id,
              lanes.rowOf.get(v.appointmentId) ?? 0,
              isToday && v.appointmentId === tech.activeAppointmentId,
              v.appointmentId === tech.nextAppointmentId,
            ),
          );
        })()}
        {shadow(tech.id)}
      </div>
    </div>
  );

  // a visit whose day went by while it was still booked or sent (QA-764): nobody went, so the panel
  // offers to rebook it, never to dispatch it, take it back, give it out or record steps on it
  const missed = !!sel && pastState(sel, tz, today) === 'missed';
  /** The open visit's actions, for a dispatcher: Riverstone's panel and Blueprint's SidePanel both. */
  const panelActions = sel && dispatching && (
    <div className="actions">
      {(sel.status === 'scheduled' || sel.status === 'dispatched') &&
        !missed &&
        (bp ? (
          <BpTechPicker
            visit={sel}
            techs={board?.technicians ?? []}
            busy={busy}
            onAssign={(lead, helpers) => assignFromPanel(sel, lead, helpers)}
          />
        ) : (
          <TechPicker
            visit={sel}
            techs={board?.technicians ?? []}
            busy={busy}
            onAssign={(lead, helpers) => assignFromPanel(sel, lead, helpers)}
          />
        ))}
      {proposal?.visitId === sel.appointmentId &&
        (bp ? (
          <BpCheckMove
            visit={sel}
            outcome={proposal.outcome}
            start={instantOfMinute(date, proposal.place.start, tz)}
            timeZone={tz}
            onCancel={() => {
              setProposal(null);
              setDragSay('Not moved. It stays where it was.');
            }}
            onMove={(window) => {
              const p = proposal;
              if (p.picked) {
                setProposal(null);
                return assign({ ...sel, rowVersion: p.rowVersion }, p.picked.lead, p.picked.helpers).then(
                  (ok): MoveResult => (ok ? 'moved' : 'refused'),
                );
              }
              const key = p.key ?? newKey();
              if (!p.key) setProposal({ ...p, key });
              return moveVisit(sel, p.fromTechId, p.place, window, p.rowVersion, key).then((r) => {
                if (r !== 'no-answer') setProposal(null);
                return r;
              });
            }}
          />
        ) : (
          <CheckMove
            visit={sel}
            outcome={proposal.outcome}
            start={instantOfMinute(date, proposal.place.start, tz)}
            timeZone={tz}
            busy={busy}
            onCancel={() => {
              setProposal(null);
              setDragSay('Not moved. It stays where it was.');
            }}
            onMove={(window) => {
              const p = proposal;
              if (p.picked) {
                setProposal(null);
                return assign({ ...sel, rowVersion: p.rowVersion }, p.picked.lead, p.picked.helpers).then(
                  (ok): MoveResult => (ok ? 'moved' : 'refused'),
                );
              }
              const key = p.key ?? newKey();
              if (!p.key) setProposal({ ...p, key });
              return moveVisit(sel, p.fromTechId, p.place, window, p.rowVersion, key).then((r) => {
                if (r !== 'no-answer') setProposal(null);
                return r;
              });
            }}
          />
        ))}
      {/* each keyed apart from its siblings: one key shared by two of them left a copy of the
                      Move panel behind on every re-read once a dispatch showed FieldSteps (QA-706 2) */}
      {(sel.status === 'scheduled' || sel.status === 'dispatched') &&
        !proposal &&
        (bp ? (
          <BpMoveVisit
            key={`move:${sel.appointmentId}`}
            visit={sel}
            date={date}
            timeZone={tz}
            busy={busy}
            rebook={missed}
            onMove={moveFromPanel}
          />
        ) : (
          <MoveVisit
            key={`move:${sel.appointmentId}`}
            visit={sel}
            date={date}
            timeZone={tz}
            busy={busy}
            rebook={missed}
            onMove={moveFromPanel}
          />
        ))}
      {/* a started or finished visit: its times, on its day (BL-158) */}
      {board &&
        !proposal &&
        (() => {
          const rule = timeRule(sel, nowIso, tz);
          if (rule !== 'started' && rule !== 'over') return null;
          const props = {
            board,
            visit: sel,
            rule,
            now: nowIso,
            timeZone: tz,
            busy,
            onRetime: (input: { start: string; end: string; confirm?: true; rowVersion: number }) =>
              retimeFromPanel(sel, input),
          };
          const key = `retime:${sel.appointmentId}`;
          return bp ? <BpRetimeVisit key={key} {...props} /> : <RetimeVisit key={key} {...props} />;
        })()}
      {sel.status === 'scheduled' && !missed && board && (
        <NextOrDispatch
          bp={bp}
          visit={sel}
          goesOut={goesOut}
          queued={queuedFor(board, sel)}
          busyTech={(() => {
            // the offer: Dispatch's refusal (the board was behind) or a tech the board shows out
            const o = offer?.appointmentId === sel.appointmentId ? offer : null;
            const b = busyTechs(board, sel)[0];
            const staffId = o?.staffId ?? b?.staffId;
            return staffId ? { staffId, name: techName(staffId) } : null;
          })()}
          line={dispatchBlocker(board, sel)}
          replace={offer?.appointmentId === sel.appointmentId ? offer.replace : null}
          busy={busy}
          onDispatch={() => void dispatchOrOffer(sel)}
          onNext={(staffId, replace) => void sendNext(sel, staffId, replace)}
          onNotNow={() => setOffer(null)}
          onUnqueue={(staffId) =>
            void act(
              'appointments.unqueueNext',
              { appointmentId: sel.appointmentId, staffId },
              `${sel.customerName}’s visit isn’t next for ${techName(staffId)} any more.`,
            )
          }
        />
      )}
      {sel.status === 'paused' && (
        // paused for parts or a return: the next visit goes on this job (gap #5)
        <AddVisit
          key={`add-visit:${sel.appointmentId}`}
          jobId={sel.jobId}
          jobNumber={sel.jobNumber}
          leadTechId={sel.leadTechId}
          timeZone={tz}
          onAdded={setToast}
        />
      )}
      {sel.status === 'dispatched' && !missed && (
        <ActionButton
          bp={bp}
          wide
          disabled={busy}
          onPress={() => void act('appointments.undispatch', { appointmentId: sel.appointmentId })}
        >
          Take back
        </ActionButton>
      )}
      {officeStep(sel) && !missed && (
        <FieldSteps
          bp={bp}
          key={`steps:${sel.appointmentId}`}
          visit={sel}
          step={officeStep(sel)!}
          techName={techName}
          busy={busy}
          onArrive={(staffId) =>
            void act(
              'appointments.arrive',
              arriveInput(sel.appointmentId, staffId),
              `${techName(staffId)} is recorded as arrived.`,
            )
          }
          onPause={(staffId, reason) =>
            act(
              'appointments.pause',
              pauseInput(sel.appointmentId, staffId, reason),
              `Paused for ${techName(staffId)}: ${pauseWords(reason)}.`,
            )
          }
        />
      )}
    </div>
  );

  // ── Blueprint's day board, from ui-web's round 2 components (ROUND-2.md, the assembled preview) ──
  // BoardBand over the Board (TimeScale with the corner, the lanes, No one yet), then the map strip and
  // the status bar's table; the open visit in a SidePanel; a technician's TechMenu. The state above is
  // the board's; these only draw it.
  const bpDay = bp && view === 'day';
  const nowMinute = isToday && loaded ? Math.round(localHours(nowIso, tz) * 60) : null;
  const viewerRole = techMenuRole(nav.roles, nav.permissions ?? []);
  const fixer = fixesHours(viewerRole);
  const visitOn = (techId: string | null, v: BoardVisit, tech?: BoardTech): VisitOn => ({
    tz,
    date,
    today,
    techId,
    overdue: overdueOf(v),
    now: nowMinute,
    techName,
    isNow: !!tech && isToday && v.appointmentId === tech.activeAppointmentId,
    isNext: !!tech && v.appointmentId === tech.nextAppointmentId,
    statusLabels: STATUS_LABEL,
    panelOpen: !!selected,
  });
  const laneVisit = (v: BoardVisit, techId: string | null, tech?: BoardTech): LaneVisit => {
    const lv = laneVisitOf(v, visitOn(techId, v, tech));
    return {
      ...lv,
      // greyed out by a filter: its name says so, not only its colour (QA-845)
      ...(keeps(v, filter) ? {} : { label: `${lv.label}, left out by the filter` }),
      // picked up, or waiting for its check where it was: dashed and muted (BL-124)
      ...(dragClass(v.appointmentId) ? { drag: 'lifted' as const } : {}),
    };
  };
  /** What a visit's button carries for the drag (BL-124) and the filter (dispatch parity 5). */
  const visitProps =
    (techId: string | null) =>
    (lv: LaneVisit): ButtonHTMLAttributes<HTMLButtonElement> => {
      const v = allVisits.get(lv.id);
      if (!v) return {};
      const movable = dispatching && !cantPickUp(v);
      return {
        draggable: false,
        onPointerDown: onPressDown,
        onKeyDown: onDragKey,
        onBlur: onDragBlur,
        ...(movable && techId ? { 'aria-describedby': 'board-drag-how' } : {}),
        // the drag's hooks, and a visit the filter leaves out, drawn faded (dispatch parity 5)
        ...({
          'data-drag-visit': v.appointmentId,
          'data-drag-from': techId ?? 'none',
          'data-faded': keeps(v, filter) ? undefined : '',
        } as object),
      };
    };
  /** Where a visit shows while it's dragged, waiting for its check, or landing: its ghost on that lane. */
  const ghostOn = (laneId: string): LaneVisit | null => {
    const g = drag
      ? { visitId: drag.visitId, place: drag.place, bad: dragOutcome?.kind === 'refuse', pending: false }
      : proposal
        ? { visitId: proposal.visitId, place: proposal.place, bad: false, pending: true }
        : landing
          ? { visitId: landing.visitId, place: landing.place, bad: false, pending: false }
          : null;
    if (!g || g.place.techId !== laneId) return null;
    const v = allVisits.get(g.visitId);
    if (!v) return null;
    const start = instantOfMinute(date, g.place.start, tz);
    const end = instantOfMinute(date, g.place.end, tz);
    return {
      id: `ghost:${v.appointmentId}`,
      start: g.place.start,
      end: g.place.end,
      state: visitState(v, laneId),
      time: windowText({ start, end, arrivalWindow: null }, tz),
      timeShort: startWord(g.place.start),
      customer: v.customerName,
      job: g.pending ? 'Moving here' : `#${v.jobNumber}`,
      label: `${v.customerName}’s visit would go here`,
      drag: g.bad ? 'ghost-bad' : 'ghost',
    };
  };
  /** A technician's lane: its head (name, now, their day or a problem), their visits, their time away. */
  const bpLane = (tech: BoardTech) => {
    const pen = penAt(penIndex.get(tech.id) ?? 0);
    const td = tech.days?.find((d) => d.date === date);
    // BL-014: away all day, off, or on non-job time now; the rest of their time away, said in the label
    const { chip: away, more: awayMore } = laneAway(td, isToday, tz, nowIso);
    const cl = clockLine(tech.clock, td, {
      date,
      today,
      now: nowIso,
      timeZone: tz,
      booked: notStartedFor(tech.visits, tech.id, date, tz),
    });
    const extra = grouping.laneFor(tech);
    const person = teamDay?.people.find((p) => p.staffId === tech.id);
    const problems = problemsOf(person);
    // an earlier day's time to fix (the timesheet's words), else a clock-out missed in the last week
    const problem = problems[0]
      ? problemLaneWords(problems[0])
      : person?.leftOpenOn
        ? problemLaneWords({ kind: 'noClockOut', date: person.leftOpenOn })
        : leftOpen.some((p) => p.staffId === tech.id)
          ? 'Clock-out missed'
          : null;
    const late = laneDayLine(tech.visits, overdueOf, tz);
    return (
      // qa/web's hook (data-lane: a drop's lane); drawn as if it weren't there (display: contents)
      <div
        className="lane-row"
        data-lane={tech.id}
        key={tech.id}
        onPointerDown={extra.wrap.onPointerDown}
        onClickCapture={extra.wrap.onClickCapture}
      >
        <BoardLane
          laneId={tech.id}
          grip={extra.grip}
          moved={extra.moved}
          lifted={extra.lifted}
          name={tech.name}
          shortName={shortName(tech.name)}
          pen={pen}
          detail={
            away
              ? { words: away }
              : laneClockWords(tech.clock, cl, {
                  date,
                  today,
                  timeZone: tz,
                  booked: notStartedFor(tech.visits, tech.id, date, tz),
                  // the job they're out on now, as the classic board's Out chip named it
                  out: outChip(tech),
                })
          }
          day={late.bad || !extra.usually ? late : extra.usually}
          problem={problem}
          visits={tech.visits.map((v) => laneVisit(v, tech.id, tech))}
          off={laneOffOf(tech, date, tz, endHour, nowIso)}
          away={awayMore}
          selectedId={selected}
          onSelectVisit={(id) => !pick && setSelected(id)}
          onOpenVisit={(id) => {
            if (pick) return;
            setSelected(null);
            const v = allVisits.get(id);
            if (v) openTheJob(v.jobId);
          }}
          {...(fixer && onOpenHours ? { onTimesheet: () => onOpenHours(tech.id, date) } : {})}
          // QA-387: while a visit is picked up, a tap on the name gives it (BoardLane), never the menu
          onTechMenu={(at) => {
            if (!picked) setMenu({ techId: tech.id, at });
          }}
          isTarget={
            drag?.edge === 'move' && drag.place.techId === tech.id && drag.place.techId !== drag.fromTechId
          }
          visitProps={visitProps(tech.id)}
          ghost={ghostOn(tech.id)}
          pick={
            picked
              ? { label: `Give ${picked.customerName}’s visit to ${tech.name}`, onPick: () => place(tech.id) }
              : null
          }
        />
      </div>
    );
  };
  // the corner's filter: State, then what the day's visits offer, then the technicians shown
  const filterCount =
    (only ? 1 : 0) +
    (['unit', 'type', 'tag'] as const).filter((k) => filter[k]).length +
    (onlyTech ? 1 : 0) +
    (hiddenTechs.size ? 1 : 0);
  const techs = loaded ? board.technicians : [];
  const corner = (
    <BoardCorner
      {...grouping.corner}
      filterCount={filterCount}
      filterOpen={filterOpen}
      onFilterOpenChange={setFilterOpen}
      onClear={() => {
        setFilter(NO_FILTER);
        setOnlyTech(null);
        setHiddenTechs(new Set());
      }}
      filters={
        <>
          <ChoiceGroup
            label="State"
            selectionMode="single"
            value={only ?? 'all'}
            onChange={(v) => setOnly(v === 'all' ? null : v)}
          >
            <Choice value="all" count={forBar.length}>
              All
            </Choice>
            {counts.map((c) => (
              <Choice key={c.status} value={c.status} count={c.count}>
                {STATUS_LABEL[c.status] ?? c.status}
              </Choice>
            ))}
          </ChoiceGroup>
          {(
            [
              // the job's department, not the technician's division (QA-845)
              ['unit', 'Job’s department', opts.units],
              ['type', 'Job type', opts.types],
              ['tag', 'Tag', opts.tags],
            ] as const
          ).map(
            ([key, label, list]) =>
              list.length > 0 && (
                <ChoiceGroup
                  key={key}
                  label={label}
                  selectionMode="single"
                  value={filter[key] ?? 'all'}
                  onChange={(v) => setFilter({ ...filter, [key]: v === 'all' ? null : v })}
                >
                  <Choice value="all">All</Choice>
                  {list.map((o) => (
                    <Choice key={o.id} value={o.id}>
                      {o.name}
                    </Choice>
                  ))}
                </ChoiceGroup>
              ),
          )}
          {techs.length > 1 && (
            <ChoiceGroup
              label="Technician"
              selectionMode="single"
              value={onlyTech ?? 'all'}
              onChange={(v) => setOnlyTech(v === 'all' ? null : v)}
            >
              <Choice value="all">Everyone</Choice>
              {techs.map((t) => (
                <Choice key={t.id} value={t.id}>
                  {t.name}
                </Choice>
              ))}
            </ChoiceGroup>
          )}
          {hiddenTechs.size > 0 && (
            <ChoiceGroup
              label="Hidden today"
              value={[...hiddenTechs]}
              onChange={(v) => setHiddenTechs(new Set(v))}
            >
              {techs
                .filter((t) => hiddenTechs.has(t.id))
                .map((t) => (
                  <Choice key={t.id} value={t.id}>
                    {t.name}
                  </Choice>
                ))}
            </ChoiceGroup>
          )}
        </>
      }
    />
  );
  // the map strip: the day's map, every route; Show on map narrows it to one technician's
  const pens = loaded ? pensBy(board.technicians) : new Map<string, number>();
  const fullMap = loaded && allVisits.size ? dayMapProps(board, pens, nowIso) : null;
  const mapOf = (id: string | null) =>
    fullMap && id
      ? {
          ...fullMap,
          pins: fullMap.pins.filter((p) => allVisits.get(p.id)?.techIds.includes(id)),
          routes: fullMap.routes.filter((r) => r.id === id),
          unplaced: fullMap.unplaced.filter((u) => allVisits.get(u.id)?.techIds.includes(id)),
        }
      : fullMap;
  const mapData = mapOf(mapTech);
  // the status bar and its table: a day's state, or a list (BL-127)
  const dayTab = isListTab(boardTab) ? null : boardTab;
  const lookVisits = looks.map((l) => l.visit);
  const bar = statusGroups({
    day: theDay.filter((v) => keeps(v, filter, 'status') && techKept(v)),
    looks: looks.length,
    waiting: loaded ? board.unassigned.length : 0,
    paused: pausedTotal,
    lists: lists?.counts ?? null,
    // Needs a look is a dispatcher's, as the classic board's tray was
    canLook: canDispatch,
  });
  const lateWordsOf = (v: BoardVisit): string | null => {
    const o = overdueOf(v);
    return o ? lookWhy(o, tz) : null;
  };
  const rowPen = (id: string) => (penIndex.has(id) ? penAt(penIndex.get(id)!) : null);
  // Paused: a table for each reason, why and since when on every row (dispatch parity 4)
  const pausedByReason =
    dayTab === 'paused' ? pausedTables(pausedList, { tz, date, today, techName, pen: rowPen }) : null;
  const tableRows2 = dayTab
    ? dayTabVisits(dayTab, {
        day: theDay.filter((v) => keeps(v, filter) && techKept(v)),
        looks: lookVisits,
        unassigned: loaded ? board.unassigned : [],
        paused: pausedList,
      }).map((v) =>
        visitRow(v, {
          tz,
          date,
          techName,
          pen: rowPen,
          why: dayTab === 'look' ? lateWordsOf(v) : null,
        }),
      )
    : !listsFailed && lists?.tab === tab
      ? listRows(lists, tz, today, techName).map(listJobRow)
      : [];
  const jobOfListRow = new Map(
    !dayTab && lists?.tab === tab
      ? listRows(lists, tz, today, techName).map((r) => [r.key, r.jobId] as const)
      : [],
  );
  const tableEmpty =
    dayTab === 'look'
      ? 'No visits need a look'
      : dayTab === 'nobody'
        ? allVisits.size
          ? 'Every visit has someone'
          : 'Nothing waiting'
        : dayTab
          ? 'No visits in this state on this day'
          : LIST_EMPTY[tab];

  const bpMain = (
    <main
      ref={boardEl}
      className={`bp-day${phase === 'wait' ? ' ldwait' : ''}`}
      aria-busy={!loaded && phase !== 'error'}
      hidden={view === 'week'}
    >
      <h1 className="sr-only">Day board</h1>
      <BoardBand
        date={dateWords}
        // while imported, the figures are as of the last read, not now
        asOf={
          mirroring && board && importReadAt(board, tz)
            ? importReadAt(board, tz)
            : isToday
              ? `${nowClock.time} ${nowClock.suffix}`
              : null
        }
        figures={
          figures
            ? bandFigures(
                // a day gone by: its visits nobody went to, counted (QA-764)
                date < today
                  ? { ...figures, missed: theDay.filter((v) => pastState(v, tz, today) === 'missed').length }
                  : figures,
              )
            : [{ words: loaded ? 'Nothing booked' : 'Getting the day' }]
        }
        isToday={isToday}
        density={compact ? 'compact' : 'comfortable'}
        readOnly={mirroring}
        canBook={canBook}
        showDensity={canHover}
        onDay={(step) => setDate(step === 'today' ? today : shiftDate(date, step === 'prev' ? -1 : 1))}
        onDensity={(d: FlDensity) => {
          setDensity(d);
          keepDensity(nav.tenant, who, d);
        }}
        onBook={() => nav.onView('book')}
        toSchedule={{ count: toSchedule, href: '#/tobook', onPress: () => nav.onView('tobook') }}
        editing={grouping.band}
      />
      {picked && (
        // tap-to-give (BL-124): over the board, never over its lanes
        <p className="bp-pick" role="status">
          <span>Now press a technician’s lane to give them {picked.customerName}’s visit.</span>
          <FlButton size="sm" onPress={() => setPick(null)}>
            Cancel
          </FlButton>
        </p>
      )}
      {loaded &&
        (() => {
          // what the filters leave out, said over the board, with Clear right there (QA-845)
          const nameIn = (list: readonly { id: string; name: string }[], id: string | null) =>
            id ? list.find((o) => o.id === id)?.name : undefined;
          const said = filterSummary(
            {
              ...(onlyTech ? { tech: techName(onlyTech) } : {}),
              hidden: hiddenTechs.size,
              ...(nameIn(opts.units, filter.unit) ? { unit: nameIn(opts.units, filter.unit)! } : {}),
              ...(nameIn(opts.types, filter.type) ? { type: nameIn(opts.types, filter.type)! } : {}),
              ...(nameIn(opts.tags, filter.tag) ? { tag: nameIn(opts.tags, filter.tag)! } : {}),
              ...(only ? { state: STATUS_LABEL[only] ?? only } : {}),
            },
            tableRows.length,
            theDay.length,
          );
          return said ? (
            <p className="bp-say" role="status">
              {said}{' '}
              <FlLink
                onPress={() => {
                  setFilter(NO_FILTER);
                  setOnlyTech(null);
                  setHiddenTechs(new Set());
                }}
              >
                Clear filters
              </FlLink>
            </p>
          ) : null;
        })()}
      <div className="bp-board" ref={bpWrap}>
        <DayBoard
          label={`Day board, ${dateWords}`}
          from={DAY_START_HOUR * 60}
          to={endHour * 60}
          now={nowMinute}
          density={compact ? 'compact' : 'comfortable'}
          readOnly={mirroring}
          {...(groups ? { names: 'fit' as const } : {})}
        >
          <TimeScale corner={corner} />
          {!loaded &&
            (cache.lanes.length ? cache.lanes : [0, 1, 2].map((i) => ({ id: `ph${i}`, name: '' }))).map(
              (t) => <BoardLane key={t.id} name={t.name} visits={[]} />,
            )}
          {loaded &&
            (groups
              ? groups.map((g, i) => {
                  // arranging: every technician and every division, nobody filtered out or folded away
                  const arranging = grouping.arranging;
                  const shown = arranging ? g.techs : g.techs.filter((t) => shownTech(t.id));
                  if (!shown.length && !arranging) return null;
                  const lines = divisionLines(g.techs, overdueOf);
                  const head = grouping.headFor(g);
                  return (
                    <DivisionGroup
                      key={g.key}
                      first={i === 0}
                      // the whole name: the heading wraps a long one in its row, never cut at 40
                      // letters, which dropped the words that tell divisions apart (QA-841)
                      name={g.name}
                      summary={head.empty ? head.emptyLine : lines.summary}
                      bad={lines.bad}
                      empty={head.empty}
                      edit={head.edit}
                      isTarget={head.isTarget}
                      slot={head.slot}
                      lifted={head.lifted}
                      {...(head.dropKey ? { dropKey: head.dropKey } : {})}
                      folded={!arranging && folded.has(g.key)}
                      onToggle={() => foldGroup(g.key)}
                      days={shown.map((t) => ({
                        name: t.name,
                        pen: penAt(penIndex.get(t.id) ?? 0),
                        visits: t.visits.map((v) => ({
                          start: minuteOf(v.start, date, tz),
                          end: minuteOf(v.end, date, tz),
                          done: v.status === 'closed',
                        })),
                      }))}
                    >
                      {shown.map(bpLane)}
                    </DivisionGroup>
                  );
                })
              : board.technicians.filter((t) => shownTech(t.id)).map(bpLane))}
          {loaded && grouping.addRow}
          {loaded && (
            // the visits nobody has yet, by their time: a tap picks one up to give, or drag it to a lane;
            // a visit dropped here goes back to nobody
            <div className="lane-row" data-lane="none">
              <BoardLane
                kind="unassigned"
                name="No one yet"
                day={{
                  words: board.unassigned.length
                    ? `${board.unassigned.length} to give out`
                    : allVisits.size
                      ? 'Every visit has someone'
                      : 'Nothing waiting',
                }}
                visits={board.unassigned.map((v) => laneVisit(v, null))}
                selectedId={selected}
                onSelectVisit={(id) => {
                  const again = pick === id;
                  if (dispatching) setPick(again ? null : id);
                  setSelected(again ? null : id);
                }}
                onOpenVisit={(id) => {
                  setPick(null);
                  setSelected(null);
                  const v = allVisits.get(id);
                  if (v) openTheJob(v.jobId);
                }}
                visitProps={visitProps(null)}
              />
            </div>
          )}
        </DayBoard>
      </div>
      {loaded && !board.technicians.length && <p className="bp-say">{noTechniciansLine(true)}</p>}
      {loaded && board.technicians.length > 0 && allVisits.size === 0 && (
        <p className="bp-say">{isToday ? 'Nothing is booked today.' : `Nothing is booked for ${dayName}.`}</p>
      )}
      {/* the slot for the 3 s line and the error, there from the first frame so nothing moves */}
      {phase === 'error' ? (
        <p className="sayline late ldline" id="loadError">
          Couldn't get {what}.{' '}
          <FlButton size="sm" onPress={() => setAttempt((n) => n + 1)}>
            Try again
          </FlButton>
        </p>
      ) : (
        <p className="sayline ldline" id="loadSay">
          {phase === 'slow' ? `Getting ${what}…` : ''}
        </p>
      )}
      <p className="ld-live" aria-live="polite" id="ldLive">
        {phase === 'slow'
          ? `Getting ${what}…`
          : phase === 'error'
            ? `Couldn't get ${what}. Try again.`
            : said}
      </p>
      <p className="sr-only" id="board-drag-how">
        Arrow keys move it; up and down give it to another technician; Shift with an arrow changes its end.
        Enter moves it, Escape puts it back.
      </p>
      <p className="sr-only" aria-live="assertive" id="dragLive">
        {dragSay}
      </p>
      {mapData && (
        <div className="bp-below" ref={mapEl}>
          <MapStrip
            map={
              mapData.pins.length ? (
                <DayMapOrGoogle
                  data={mapData}
                  label="The day’s visits on the map"
                  selected={selected}
                  onPick={setSelected}
                />
              ) : (
                <p className="bp-say">
                  No visit {mapTech ? 'of theirs ' : ''}today has a location for its address.
                </p>
              )
            }
            routes={techs
              .filter((t) => (mapTech ? t.id === mapTech : shownTech(t.id)))
              .map((t) => routeOf(t, penAt(penIndex.get(t.id) ?? 0)))}
          />
          {mapData.pins.length > 0 && mapData.unplaced.length > 0 && (
            // QA-852: a visit whose address has no location isn't dropped without a word
            <p className="bp-say">
              Not on the map, no location for the address:{' '}
              {mapData.unplaced.map((u, i) => (
                <span key={u.id}>
                  {i > 0 && '; '}
                  <FlLink onPress={() => setSelected(u.id)}>{u.title}</FlLink> ({u.line})
                </span>
              ))}
              .
            </p>
          )}
          {mapTech && (
            <p className="bp-say">
              {techName(mapTech)}’s route only.{' '}
              <FlLink onPress={() => setMapTech(null)}>Show everyone’s</FlLink>
            </p>
          )}
        </div>
      )}
      {loaded && (
        <section className="bp-below" aria-label="Visits and jobs by state">
          <StatusBar
            label="Visits and jobs by state"
            groups={bar}
            // each group says what it counts (QA-768): the board's day, then any day
            scopes={statusScopes(date, today)}
            selected={boardTab}
            onChange={(id) => {
              setBoardTab(id as BoardTab);
              if (isListTab(id)) setTab(id);
            }}
          />
          {!dayTab && listsFailed && (
            <p className="bp-say">Couldn’t get this list. It tries again with the board.</p>
          )}
          {pausedByReason?.length ? (
            pausedByReason.map((g) => (
              <JobTable
                key={`paused:${g.key}`}
                label={`Paused: ${g.title}`}
                title={g.title}
                rows={g.rows}
                columns={PAUSED_COLUMNS}
                onOpen={(id) => setSelected(selected === id ? null : id)}
              />
            ))
          ) : (
            <JobTable
              key={boardTab}
              label={dayTab ? 'The day’s list' : 'Jobs by state'}
              rows={tableRows2}
              columns={dayTab === 'look' ? LOOK_COLUMNS : dayTab ? DAY_COLUMNS : LIST_COLUMNS}
              empty={tableEmpty}
              onOpen={(id) => {
                if (dayTab) return setSelected(selected === id ? null : id);
                const jobId = jobOfListRow.get(id);
                if (jobId) openTheJob(jobId);
              }}
            />
          )}
          {dayTab === 'paused' && pausedTotal > pausedList.length && (
            // the board holds the oldest 100; the rest on asking
            <p className="bp-say">
              {pausedTotal - pausedList.length} more, paused more recently.{' '}
              {!showAllPaused && (
                <FlLink onPress={() => setShowAllPaused(true)}>Show all {pausedTotal}</FlLink>
              )}
            </p>
          )}
          {!dayTab && lists?.tab === tab && lists.next && (
            <p className="bp-say">
              {listShownText(lists)}{' '}
              <FlLink isDisabled={listsMore} onPress={moreOfList}>
                {listsMore ? 'Loading…' : 'Load more'}
              </FlLink>
            </p>
          )}
        </section>
      )}
    </main>
  );

  // the open visit, beside the board: what it is, what's wrong, its fields; a dispatcher's actions under
  const bpPanel = bpDay && sel && (
    <SidePanel
      // its own start and end (QA-762); the arrival window, where it differs, is a field below
      // its own start and end, joined so qa/web's "The <h:mm> visit at" still finds it
      label={`The ${clock(sel.start, tz).time}–${clock(sel.end, tz).time} visit at ${sel.customerName}`}
      title={panelTitle(sel, {
        tz,
        date,
        dayText: (d) => dateText(d, 'UTC', { year: false, weekday: 'short' }),
      })}
      sub={`Job #${sel.jobNumber}, ${sel.techIds.length ? sel.techIds.map(techName).join(', ') : 'no one yet'}`}
      note={panelNote(sel, overdueOf(sel), tz, today)}
      links={[{ label: 'Open the job', onPress: () => openTheJob(sel.jobId, sel.appointmentId) }]}
      onClose={() => setSelected(null)}
      // its top on the block it opened from, else the board's top (Amir, 26 Oct: so the eye doesn't travel)
      anchor={() => ({
        from:
          wrapEl.current?.querySelector(`.fl-lane [data-drag-visit="${CSS.escape(sel.appointmentId)}"]`) ??
          null,
        within: wrapEl.current,
      })}
    >
      {toast && <Toast place="panel">{toast}</Toast>}
      {undoToast('in-panel')}
      <KeyValue
        items={panelFields(sel, {
          techName,
          statusLabels: STATUS_LABEL,
          pausedSince: sel.status === 'paused' ? pausedSince(sel, tz, today) : null,
          today,
          tz,
        })}
      />
      {panelActions && <div className="bp-panel-actions">{panelActions}</div>}
      {canDispatch && mirroring && <p className="bp-say">Read only while the company’s records load.</p>}
    </SidePanel>
  );

  // a technician's menu: their day so far on top, then where to go, how to reach them, the board
  const menuTech = bpDay && menu && loaded ? board.technicians.find((t) => t.id === menu.techId) : undefined;
  const bpMenu = (() => {
    if (!menu || !menuTech) return null;
    const t = menuTech;
    const pen = penAt(penIndex.get(t.id) ?? 0);
    const person = teamDay?.people.find((p) => p.staffId === t.id);
    const truck = t.truck ?? null;
    const day = person
      ? techDayOf(
          person,
          { visits: t.visits, truck },
          { pen, date: today, timeZone: tz, now: nowMinute ?? 0 },
        )
      : placeholderDay(t.name, pen, truck);
    const status = !online ? 'offline' : person ? 'live' : teamDay || teamDayFailed ? 'failed' : 'loading';
    const asOf = teamDay ? Math.round(localHours(teamDay.asOf, tz) * 60) : (nowMinute ?? 0);
    const problems = problemsOf(person);
    const first = t.name.split(/\s+/)[0] || t.name;
    const fixDay = problems[0]?.date ?? null;
    const phone = phones.get(t.id) ?? null;
    // the quick edit of a day of theirs; the board's visits for them come along when it's the board's day
    const openQuick = (on: string) => {
      setMenu(null);
      setQuickEdit({
        staffId: t.id,
        name: t.name,
        date: on,
        pen,
        booked:
          on === date
            ? t.visits
                .map((v) => laneVisit(v, t.id, t))
                .filter((lv) => lv.state !== 'cancelled')
                .map((lv) => ({
                  id: lv.id,
                  jobId: null,
                  customer: lv.customer,
                  startMinute: lv.start,
                  endMinute: lv.end,
                  drawn: lv.state === 'done' ? 'done' : lv.state === 'working' ? 'working' : 'booked',
                  time: lv.time,
                  job: lv.job,
                }))
            : [],
      });
    };
    return (
      <TechMenu
        at={menu.at}
        onClose={() => setMenu(null)}
        day={day}
        asOf={asOf}
        status={status}
        viewer={{ role: viewerRole, inventory: (nav.permissions ?? []).includes('inventory.manage') }}
        phone={phone ? phoneText(phone) : null}
        canMessage={false}
        problems={problems.map(problemCardWords)}
        problemsNote={problemsNoteWords(problems, first)}
        {...(fixer && fixDay
          ? { fix: { label: `Fix ${dayWord(fixDay)}…`, onFix: () => openQuick(fixDay) } }
          : {})}
        {...(fixer && fixDay !== today
          ? { onEditHours: () => openQuick(today), editHoursHint: 'Today' }
          : {})}
        onSelectVisit={setSelected}
        onRetry={() => setTeamDayTry((n) => n + 1)}
        // concept A: Today in (anyone who dispatches) and their home (owners and managers), at once
        more={grouping.techMore(t)}
        onAction={(a) => {
          const action = a as string;
          if (grouping.techAction(t, action)) return;
          if (action === 'timesheet') onOpenHours?.(t.id, today);
          else if (action === 'truck' && truck) onOpenTruck?.(truck.locationId);
          else if ((action === 'call' || action === 'text') && phone)
            window.location.assign(`${action === 'call' ? 'tel' : 'sms'}:${phone}`);
          else if (action === 'map') {
            setMapTech(t.id);
            requestAnimationFrame(() => mapEl.current?.scrollIntoView({ block: 'nearest' }));
          } else if (action === 'only') setOnlyTech(t.id);
          else if (action === 'hide') {
            setHiddenTechs((h) => new Set([...h, t.id]));
            setSaid(`${t.name} is hidden for today. The filter shows them again.`);
          }
        }}
      />
    );
  })();

  return (
    <div className={bp ? 'app bp-sch' : 'app'} hidden={hidden}>
      {bpDay ? null : (
        <Header
          nav={nav}
          title={bp ? (view === 'week' ? 'The week' : boardTitle(date, localToday(tz))) : 'Dispatch'}
          toolbar={!bp}
          // BL-100: a dispatcher's board starts under one toolbar row, so no lede; someone who can only
          // watch it, or a dispatcher while it mirrors, still gets the line that says why
          {...(bp
            ? { lede: bpLede }
            : dispatching
              ? {}
              : {
                  lede: canDispatch
                    ? `The techs’ day, updated as they work. ${MIRROR_LINE}`
                    : 'The techs’ day, updated as they work. Dispatchers give the visits to the techs.',
                })}
        >
          {/* Blueprint has no Week (Amir, 26 Oct): no toggle here, so the title block keeps two actions
            (DESIGN.md §3) */}
          {!bp && (
            <div className="seg" role="group" aria-label="Board view">
              {(['day', 'week'] as const).map((v) => (
                <button
                  key={v}
                  className={`btn quiet${view === v ? ' on' : ''}`}
                  aria-pressed={view === v}
                  onClick={() => setView(v)}
                >
                  {v === 'day' ? 'Day' : 'Week'}
                </button>
              ))}
            </div>
          )}
          <nav className="seg" aria-label={view === 'week' ? 'Week' : 'Day'}>
            <button
              className="btn quiet icon"
              onClick={() => setDate(shiftDate(date, view === 'week' ? -7 : -1))}
              aria-label={view === 'week' ? 'Previous week' : 'Previous day'}
            >
              ‹
            </button>
            <button
              className={`btn quiet${(view === 'week' ? weekOf(date)[0] === weekOf(localToday(tz))[0] : isToday) ? ' on' : ''}`}
              onClick={() => setDate(localToday(tz))}
            >
              {view === 'week' ? 'This week' : 'Today'}
            </button>
            <button
              className="btn quiet icon"
              onClick={() => setDate(shiftDate(date, view === 'week' ? 7 : 1))}
              aria-label={view === 'week' ? 'Next week' : 'Next day'}
            >
              ›
            </button>
          </nav>
          {!bp && <div className="date">{dateWords}</div>}
          {!bp && controls}
          {bp && canBook && (
            <button className="btn primary" onClick={() => nav.onView('book')}>
              Book a visit
            </button>
          )}
        </Header>
      )}

      {view === 'week' && (
        <WeekView
          date={date}
          today={localToday(tz)}
          onPick={(d) => {
            setDate(d);
            setView('day');
          }}
        />
      )}
      {/* hidden, not gone, in the week: the day's board keeps its place and state for a day's cell */}
      {bp ? (
        bpMain
      ) : (
        <main
          ref={boardEl}
          className={`board${phase === 'wait' ? ' ldwait' : ''}`}
          aria-busy={!loaded && phase !== 'error'}
          hidden={view === 'week'}
        >
          {onOpenHours && leftOpen.length > 0 && (
            <div className="notice warn left-open">
              <span>
                {leftOpen.length === 1 ? 'A clock-out was missed' : 'Clock-outs were missed'} in the last
                week: correct the hours.
              </span>
              {leftOpen.map((p) => (
                <button key={p.staffId} className="btn quiet" onClick={() => onOpenHours(p.staffId)}>
                  {p.name}’s hours ›
                </button>
              ))}
            </div>
          )}
          <div className="board-main">
            {/* on a phone the lanes scroll sideways in here, so it takes focus then (axe scrollable-region-focusable) */}
            <div className="lanes-wrap" ref={lanesWrap} data-region="Technicians’ lanes">
              <section
                className={`lanes${compact ? ' compact' : ''}`}
                aria-label="Technicians"
                style={{ '--span': endHour - DAY_START_HOUR } as CSSProperties}
              >
                {nowLeft !== null && nowLeft >= 0 && nowLeft <= 100 && (
                  // One "now" across every lane: an ink line, and the day's one sun saying the time.
                  <div
                    className="nowmark"
                    style={{ '--at': nowLeft / 100 } as CSSProperties}
                    aria-hidden="true"
                  >
                    <div className="now">
                      Now {nowClock.time} {nowClock.suffix}
                    </div>
                  </div>
                )}
                {/* the hours, over the lanes; on a phone their names column stays put over them too */}
                <div className="hours-row" aria-hidden="true">
                  <span className="who" />
                  <div className="hours" aria-hidden="true">
                    {Array.from({ length: endHour - DAY_START_HOUR + 1 }, (_, i) => (
                      <span key={i} style={{ left: `${(i / (endHour - DAY_START_HOUR)) * 100}%` }}>
                        {((DAY_START_HOUR + i - 1) % 12) + 1}
                      </span>
                    ))}
                  </div>
                </div>
                {!loaded &&
                  (cache.lanes.length
                    ? cache.lanes
                    : [0, 1, 2].map((i) => ({ id: `ph${i}`, name: '', initials: '', color: null }))
                  ).map((tech) => (
                    <div className="lane-row" key={tech.id}>
                      <div className="who">
                        <span className="avatar" style={{ background: tech.color ?? 'var(--surface-2)' }}>
                          {tech.initials}
                        </span>
                        <span className="name">{tech.name || blank}</span>
                      </div>
                      <div className="lane" aria-label={tech.name ? `${tech.name}'s day` : undefined} />
                    </div>
                  ))}
                {loaded &&
                  (groups
                    ? groups.flatMap((g) => [
                        groupHead(g),
                        ...(folded.has(g.key) ? [miniLines(g)] : g.techs.map((t) => laneRow(t))),
                      ])
                    : board.technicians.map((tech) => laneRow(tech)))}
                {loaded && !board.technicians.length && <p className="muted">{noTechniciansLine(false)}</p>}
                {loaded && board.technicians.length > 0 && allVisits.size === 0 && (
                  <p className="muted">
                    {isToday ? 'Nothing is booked today.' : `Nothing is booked for ${dayName}.`}
                  </p>
                )}
                {/* the slot for the 3 s line and the error, there from the first frame so nothing moves */}
                {phase === 'error' ? (
                  <p className="sayline late ldline" id="loadError">
                    Couldn't get {what}.{' '}
                    <button className="btn small soft" onClick={() => setAttempt((n) => n + 1)}>
                      Try again
                    </button>
                  </p>
                ) : (
                  <p className="sayline ldline" id="loadSay">
                    {phase === 'slow' ? `Getting ${what}…` : ''}
                  </p>
                )}
                <p className="ld-live" aria-live="polite" id="ldLive">
                  {phase === 'slow'
                    ? `Getting ${what}…`
                    : phase === 'error'
                      ? `Couldn't get ${what}. Try again.`
                      : said}
                </p>
                {/* BL-124: how a visit moves, and the drag's own words as it moves */}
                <p className="sr-only" id="board-drag-how">
                  Arrow keys move it; up and down give it to another technician; Shift with an arrow changes
                  its end. Enter moves it, Escape puts it back.
                </p>
                <p className="sr-only" aria-live="assertive" id="dragLive">
                  {dragSay}
                </p>
              </section>
              {loaded && canSetDivisions && (
                // BL-125: Setup's sheet for the board's divisions, from under the board
                <p className="div-link">
                  <button type="button" className="link-button" onClick={() => setSettingDivisions(true)}>
                    {divisions.length ? 'Change the board’s divisions' : 'Group the board by division'}
                  </button>
                </p>
              )}
            </div>
            {loaded && theDay.length > 0 && (
              // named without "visit": the panel is the board's one region called Visit
              <section className="board-table" aria-label="The day’s list">
                <h2>
                  {only ? `${STATUS_LABEL[only] ?? only}: ` : filtering ? 'Filtered: ' : ''}
                  {tableRows.length} {tableRows.length === 1 ? 'visit' : 'visits'}
                </h2>
                {tableRows.length === 0 ? (
                  <p className="muted">None on this day. All shows the rest.</p>
                ) : (
                  <div className="table-wrap" ref={scrollFocus}>
                    <table className="grid-table" aria-label="The day’s list">
                      <thead>
                        <tr>
                          {(
                            [
                              ['time', 'Time'],
                              ['customer', 'Customer'],
                              ['job', 'Job'],
                              ['status', 'State'],
                              ['techs', 'Technicians'],
                            ] as [VisitSortKey, string][]
                          ).map(([key, label]) => (
                            <th
                              key={key}
                              aria-sort={
                                sort.key === key
                                  ? sort.dir === 'asc'
                                    ? 'ascending'
                                    : 'descending'
                                  : undefined
                              }
                            >
                              <button className="th-sort" onClick={() => setSort(nextSort(sort, key))}>
                                {label}
                                {sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                              </button>
                            </th>
                          ))}
                          <th>Summary</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableRows.map((v) => (
                          <tr
                            key={v.appointmentId}
                            className={selected === v.appointmentId ? 'selected' : undefined}
                          >
                            <td className="nowrap">{windowText(v, tz)}</td>
                            <td>
                              {/* the visit opens in the panel, as a tap on its pill does */}
                              <button
                                className="cell-link"
                                onClick={() =>
                                  setSelected(selected === v.appointmentId ? null : v.appointmentId)
                                }
                              >
                                {v.customerName}
                              </button>
                            </td>
                            <td className="nowrap">
                              <button className="cell-link" onClick={() => openTheJob(v.jobId)}>
                                #{v.jobNumber}
                              </button>
                            </td>
                            <td className="nowrap">{visitStatusLabel(v, STATUS_LABEL)}</td>
                            <td>
                              {v.techIds.length ? v.techIds.map(techName).join(', ') : 'No technician yet'}
                            </td>
                            <td className="sum-cell">{plainText(v.summary)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}
          </div>

          <aside className="side">
            {canDispatch && looks.length > 0 && (
              <section className={`tray looks${folds.looks ? ' folded' : ''}`} aria-label="Needs a look">
                <TrayHead open={!folds.looks} onToggle={() => fold('looks')}>
                  Needs a look ({looks.length})
                </TrayHead>
                {!folds.looks && stAsOf && <p className="muted small st-as-of">{stAsOf}</p>}
                {!folds.looks &&
                  looks.map(({ visit: v, overdue: o }) => {
                    // BL-100: one line, the reason's mark, the time, the customer and "late 25 min"; the
                    // reason in full, the job and its techs are in its name and its title
                    const w = overdueWords(o, tz);
                    const t = clock(v.arrivalWindow?.start ?? v.start, tz);
                    const crew = v.techIds.length ? v.techIds.map(techName).join(', ') : 'No technician yet';
                    return (
                      <button
                        key={v.appointmentId}
                        className={`stone look-job line${selected === v.appointmentId ? ' selected' : ''}`}
                        aria-label={`${w.badge}: ${t.time} ${t.suffix}, ${v.customerName}, ${lookWhy(o, tz)}. ${w.line}. Job ${v.jobNumber}, ${crew}`}
                        title={`${w.line} · #${v.jobNumber} · ${crew}`}
                        onClick={() => setSelected(selected === v.appointmentId ? null : v.appointmentId)}
                      >
                        <span className={`mark ${overdueTone(o)}`} aria-hidden="true" />
                        <span className="time">
                          {t.time}
                          <small> {t.suffix}</small>
                        </span>
                        <span className="title">{v.customerName}</span>
                        <span className={`why ${overdueTone(o)}`}>{lookWhy(o, tz)}</span>
                      </button>
                    );
                  })}
              </section>
            )}
            <section
              className={`tray waiting${folds.waiting ? ' folded' : ''}`}
              data-lane="none"
              aria-label="Waiting for a technician"
            >
              <TrayHead open={!folds.waiting} onToggle={() => fold('waiting')}>
                Waiting {loaded ? `(${board.unassigned.length})` : ''}
              </TrayHead>
              {!folds.waiting && !loaded && phase !== 'error' && (
                <>
                  <div className="ph-stone tray-ph" aria-hidden="true" />
                  <div className="ph-stone tray-ph" aria-hidden="true" />
                </>
              )}
              {!folds.waiting &&
                loaded &&
                board.unassigned.map((v) => {
                  const t = clock(v.arrivalWindow?.start ?? v.start, tz);
                  // BL-100: one line, the time, the customer and the job type; the job number and the
                  // summary are in its name and its title
                  const kind = v.jobType?.name ?? v.summary;
                  const label = `${t.time} ${t.suffix}, ${v.customerName}, job ${v.jobNumber}, ${v.jobType?.name ? `${v.jobType.name}, ` : ''}${plainText(v.summary)}`;
                  return (
                    <button
                      key={v.appointmentId}
                      className={`stone tray-job line${selected === v.appointmentId ? ' selected' : ''}${drag?.visitId === v.appointmentId ? ' lifted' : ''}`}
                      draggable={false}
                      data-drag-visit={v.appointmentId}
                      data-drag-from="tray"
                      onPointerDown={onPressDown}
                      aria-label={label}
                      title={`${t.time} ${t.suffix} · ${v.customerName} · #${v.jobNumber} · ${plainText(v.summary)}`}
                      {...(dispatching ? { 'aria-pressed': pick === v.appointmentId } : {})}
                      onClick={() => {
                        // A dispatcher's tap picks it up (a second tap puts it down); the sheet shows it too.
                        const again = pick === v.appointmentId;
                        if (dispatching) setPick(again ? null : v.appointmentId);
                        setSelected(again ? null : v.appointmentId);
                      }}
                    >
                      <span className="time">
                        {t.time}
                        {/* a real space, so it copies as "10:30 a.m." */}
                        <small> {t.suffix}</small>
                      </span>
                      <span className="title">{v.customerName}</span>
                      <span className="kind">{kind}</span>
                    </button>
                  );
                })}
              {!folds.waiting && loaded && !board.unassigned.length && (
                // "everyone has a technician" only when there are visits to have one
                <p className="muted">{allVisits.size ? 'Everyone has a technician.' : 'Nothing waiting.'}</p>
              )}
              {picked && (
                <p className="pickline">
                  Now tap a technician’s lane to give them {picked.customerName}’s visit, or press Enter on
                  one.{' '}
                  <button className="btn soft small" onClick={() => setPick(null)}>
                    Cancel
                  </button>
                </p>
              )}
            </section>

            {held.length > 0 && (
              // dispatch parity 4: what's paused now, whatever its day, by reason, the oldest first
              <section className={`tray held${folds.paused ? ' folded' : ''}`} aria-label="Paused">
                <TrayHead open={!folds.paused} onToggle={() => fold('paused')}>
                  Paused ({pausedTotal})
                </TrayHead>
                {!folds.paused &&
                  held.map((g) => (
                    <Fragment key={g.reason ?? 'none'}>
                      <h3>
                        {g.label} ({g.visits.length})
                      </h3>
                      {g.visits.map((v) => {
                        // BL-100: one line, the reason's mark, the customer and since when
                        const since = pausedSince(v, tz, localToday(tz));
                        const crew = v.techIds.length
                          ? v.techIds.map(techName).join(', ')
                          : 'No technician yet';
                        return (
                          <button
                            key={v.appointmentId}
                            className={`stone look-job line${selected === v.appointmentId ? ' selected' : ''}`}
                            aria-label={`${v.customerName}, paused: ${g.label}. ${since}. Job ${v.jobNumber}, ${crew}`}
                            title={`${g.label} · ${since} · #${v.jobNumber} · ${crew}`}
                            onClick={() => setSelected(selected === v.appointmentId ? null : v.appointmentId)}
                          >
                            <span className={`mark ${g.reason ?? 'none'}`} aria-hidden="true" />
                            <span className="title">{v.customerName}</span>
                            <span className="why">{since}</span>
                          </button>
                        );
                      })}
                    </Fragment>
                  ))}
                {!folds.paused && pausedTotal > pausedList.length && (
                  // the board holds the oldest 100; the rest on asking
                  <>
                    <p className="muted">{pausedTotal - pausedList.length} more, paused more recently.</p>
                    {!showAllPaused && (
                      <button className="btn soft small" onClick={() => setShowAllPaused(true)}>
                        Show all {pausedTotal}
                      </button>
                    )}
                  </>
                )}
              </section>
            )}

            {sel && (
              <section
                className="sheet detail"
                aria-label="Visit"
                style={{ '--drawer-top': `${drawerTop}px` } as CSSProperties}
              >
                {
                  <>
                    <div className="row">
                      <span className={`chip ${STATUS_CHIP[shownStatus(sel)] ?? ''}`}>
                        {visitStatusLabel(sel, STATUS_LABEL)}
                      </span>
                      <button className="btn quiet icon" onClick={() => setSelected(null)} aria-label="Close">
                        ×
                      </button>
                    </div>
                    {/* with a visit open, its news is said in the panel, under its status, so it never sits over
                      the panel's own buttons (at 390 the panel is at the foot of the screen, where a toast was) */}
                    {toast && (
                      <p className="toast in-panel" role="status">
                        {toast}
                      </p>
                    )}
                    {undoToast('in-panel')}
                    <div className="time">
                      {/* a paused visit from the hold list may be another day's */}
                      {localDateOf(sel.start, tz) !== date &&
                        `${dateText(localDateOf(sel.start, tz), 'UTC', { year: false, weekday: 'short' })}, `}
                      {windowText(sel, tz)}
                    </div>
                    {sel.status === 'paused' && sel.pauseReason && (
                      <p className="small">
                        {sentences(
                          // handed in with the work done: done, waiting for the office (QA-830)
                          workDone(sel)
                            ? 'Handed in to the office'
                            : `Paused: ${pauseWords(sel.pauseReason)}`,
                          pausedSince(sel, tz, localToday(tz)),
                        )}
                      </p>
                    )}
                    {/* what the technician said as they paused it (QA-833) */}
                    {sel.status === 'paused' && sel.pauseNote && (
                      <p className="small">
                        {sel.pauseNote.staffId ? `${techName(sel.pauseNote.staffId)}’s note` : 'Their note'}:{' '}
                        {sel.pauseNote.body}
                      </p>
                    )}
                    {(() => {
                      const o = overdueOf(sel);
                      return (
                        o && <p className={`overdue-line ${overdueTone(o)}`}>{overdueWords(o, tz).line}</p>
                      );
                    })()}
                    <h3>{sel.customerName}</h3>
                    <p className="ink2">{sel.address}</p>
                    {/* who gets "on the way" when it isn't the customer (F-54) */}
                    {sel.siteContactName && <p className="small">Site contact: {sel.siteContactName}</p>}
                    {/* what nobody on it holds (BL-027) */}
                    {gapsText(sel.licenceGaps ?? []) && (
                      <p className="notice warn small">{gapsText(sel.licenceGaps ?? [])}</p>
                    )}
                    <p>{plainText(sel.summary)}</p>
                    <p className="muted">
                      Job #{sel.jobNumber} ·{' '}
                      {sel.techIds.length ? sel.techIds.map(techName).join(', ') : 'No technician yet'}
                    </p>
                    {/* a shared visit where they're not all at the same point: who is where */}
                    {(sel.techs?.length ?? 0) > 1 &&
                      new Set(sel.techs!.map((t) => laneStatus(sel, t.id))).size > 1 && (
                        <ul className="tech-states small" aria-label="Each technician">
                          {sel.techs!.map((t) => (
                            <li key={t.id}>
                              {techName(t.id)}: {STATUS_LABEL[laneStatus(sel, t.id)] ?? laneStatus(sel, t.id)}
                            </li>
                          ))}
                        </ul>
                      )}
                    {techMessagesWords(sel.newFromTechs) && (
                      <p className="notice info small">
                        {techMessagesWords(sel.newFromTechs)}. Open the job to read them.
                      </p>
                    )}
                  </>
                }
                <button className="btn soft wide" onClick={() => openTheJob(sel.jobId)}>
                  Open job
                </button>
                {panelActions}
                {canDispatch && mirroring && <p className="muted small">{MIRROR_LINE}</p>}
              </section>
            )}
          </aside>
        </main>
      )}

      {hover &&
        !bp &&
        view === 'day' &&
        (() => {
          // the card under the pill, or over it near the foot of the screen; never past either side
          const v = allVisits.get(hover.id);
          if (!v) return null;
          const below = hover.bottom + 220 < window.innerHeight;
          const gaps = gapsText(v.licenceGaps ?? []);
          return (
            <div
              className="hover-card sheet"
              aria-hidden="true"
              style={{
                left: Math.max(8, Math.min(hover.left, window.innerWidth - 328)),
                ...(below ? { top: hover.bottom + 6 } : { bottom: window.innerHeight - hover.top + 6 }),
              }}
            >
              <span className="time">{windowText(v, tz)}</span>
              <b>{v.customerName}</b>
              <span className="ink2">{v.address}</span>
              <span>
                #{v.jobNumber} · {plainText(v.summary)}
              </span>
              <span className="muted">
                {visitStatusLabel(v, STATUS_LABEL)} ·{' '}
                {v.techIds.length ? v.techIds.map(techName).join(', ') : 'No technician yet'}
              </span>
              {!!v.tags?.length && (
                <span className="muted">Tags: {v.tags.map((t) => t.label).join(', ')}</span>
              )}
              {gaps && <span className="gap">{gaps}</span>}
            </div>
          );
        })()}
      {/* picked up to give on a phone, the panel would cover the lanes it's given to (QA-878): it stays
          the open one (the block's aria-pressed), shown again once the visit is given or put down */}
      {panelShown({ picked: !!picked, phone: phoneWide }) && bpPanel}
      {bpMenu}
      {bp && grouping.overlay}
      {quickEdit && (
        <QuickEditDialog
          open={quickEdit}
          timeZone={tz}
          onClose={() => {
            // back to the lane the menu was opened from
            setQuickEdit(null);
            focusLaneName(quickEdit.staffId);
          }}
          onSaved={() => setTeamDayTry((n) => n + 1)}
          {...(onOpenHours
            ? {
                onTimesheet: (staffId: string, on: string) => {
                  setQuickEdit(null);
                  onOpenHours(staffId, on);
                },
              }
            : {})}
        />
      )}
      {toast &&
        !sel &&
        !undo &&
        // nothing open: the usual toast at the foot, which a tap passes through to what's under it
        (bp ? (
          <Toast>{toast}</Toast>
        ) : (
          <div className="toast board-toast" role="status">
            {toast}
          </div>
        ))}
      {(() => {
        const t = moving && loaded ? board.technicians.find((x) => x.id === moving) : undefined;
        return (
          t && (
            <MoveForDaySheet
              tech={t}
              divisions={divisions}
              date={date}
              isToday={isToday}
              dayText={dayText}
              onClose={() => setMoving(null)}
              onDone={(words) => {
                setMoving(null);
                setToast(words);
                setSaid(words);
                void load();
              }}
            />
          )
        );
      })()}
      {settingDivisions && loaded && (
        <DivisionsSheet
          divisions={divisions}
          technicians={board.technicians}
          onClose={() => setSettingDivisions(false)}
          onSaved={(words) => {
            setSettingDivisions(false);
            setToast(words);
            void load();
          }}
        />
      )}
      {!sel && undoToast('board-toast')}
      {drag && dragOutcome && bp && (
        // the note by the pointer (under the outline, for the keyboard); the live region reads it out
        <DragNote
          ref={noteEl}
          head={dragOutcome.head}
          lines={dragOutcome.lines.map((l) => ({ text: l.text, ...(l.ok == null ? {} : { ok: l.ok }) }))}
          foot={footFor(dragOutcome.kind, drag.how, dragOutcome.retime)}
          {...(dragOutcome.kind === 'refuse' ? { tone: 'refuse' as const } : {})}
        />
      )}
      {drag && dragOutcome && !bp && (
        <div className={`drag-note ${dragOutcome.kind}`} ref={noteEl} aria-hidden="true">
          <b>{dragOutcome.head}</b>
          {dragOutcome.lines.map((l, i) => (
            <span key={i} className={l.ok === false ? 'bad' : l.ok ? 'ok' : 'unk'}>
              {l.text}
            </span>
          ))}
          <i>{footFor(dragOutcome.kind, drag.how, dragOutcome.retime)}</i>
        </div>
      )}
    </div>
  );
}

/** A tray's heading (BL-100): its name and count, a button that folds the tray open or closed. */
function TrayHead({
  open,
  onToggle,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <h2>
      <button className="tray-fold" aria-expanded={open} onClick={onToggle}>
        {children}
      </button>
    </h2>
  );
}

/**
 * A scheduled visit's way out (BL-070): Dispatch when its tech is free; when they're out on another
 * visit, "Send next" queues it to go out when they finish (one queued per tech, "Replace" when one is
 * already next); a queued visit says so and can be taken back. The server sends it, never the office.
 */
function NextOrDispatch({
  bp,
  visit,
  goesOut,
  queued,
  busyTech,
  line,
  replace,
  busy,
  onDispatch,
  onNext,
  onNotNow,
  onUnqueue,
}: {
  bp: boolean;
  visit: BoardVisit;
  goesOut: string | null;
  queued: { staffId: string; name: string } | null;
  busyTech: { staffId: string; name: string } | null;
  line: string | null;
  replace: string | null;
  busy: boolean;
  onDispatch: () => void;
  onNext: (staffId: string, replace: boolean) => void;
  onNotNow: () => void;
  onUnqueue: (staffId: string) => void;
}) {
  // on Blueprint its words are the panel's muted line and its buttons a row, from the board's page sheet
  if (queued)
    return (
      <>
        <p className={bp ? 'bp-act-said' : 'muted small'}>
          <b>Next for {queued.name}.</b> It goes out when they finish the visit they’re on
          {goesOut ? `, on ${goesOut}` : ''}.
        </p>
        <ActionButton bp={bp} wide disabled={busy} onPress={() => onUnqueue(queued.staffId)}>
          Take back from next
        </ActionButton>
      </>
    );
  if (goesOut)
    return (
      <p className={bp ? 'bp-act-said' : 'muted small'}>Goes out on {goesOut}: dispatch it on the day.</p>
    );
  if (busyTech)
    return (
      <>
        <p className={bp ? 'bp-act-said' : 'muted small'}>
          {replace ?? line ?? `${busyTech.name} is out on another visit. Send this next?`}
        </p>
        <div className={bp ? 'bp-acts' : 'row'}>
          <ActionButton
            bp={bp}
            kind="primary"
            disabled={busy}
            onPress={() => onNext(busyTech.staffId, !!replace)}
          >
            {replace ? 'Replace' : 'Send next'}
          </ActionButton>
          {replace && (
            <ActionButton bp={bp} kind="quiet" disabled={busy} onPress={onNotNow}>
              Not now
            </ActionButton>
          )}
        </div>
      </>
    );
  return (
    <ActionButton bp={bp} kind="primary" wide disabled={busy || !visit.techIds.length} onPress={onDispatch}>
      Dispatch
    </ActionButton>
  );
}

/**
 * One of the open visit's actions: on Blueprint the library's Button, 34 tall and 44 at phone widths
 * (the touch size), as the panel's other controls are; the classic board keeps its own .btn.
 */
function ActionButton({
  bp,
  kind = 'soft',
  wide,
  disabled,
  onPress,
  children,
}: {
  bp: boolean;
  kind?: 'primary' | 'soft' | 'quiet';
  wide?: boolean;
  disabled?: boolean;
  onPress: () => void;
  children: ReactNode;
}) {
  if (bp)
    return (
      <FlButton
        variant={kind === 'primary' ? 'primary' : 'secondary'}
        size="touch-on-phone"
        isDisabled={disabled}
        onPress={onPress}
      >
        {children}
      </FlButton>
    );
  return (
    <button className={`btn ${kind}${wide ? ' wide' : ''}`} disabled={disabled} onClick={onPress}>
      {children}
    </button>
  );
}

/**
 * The office records a technician's step when the iPad can't (F-20): "Arrived for <tech>" while they
 * are on the way, "Pause for <tech>" with the reason while they work. Recorded as the tech's step, made
 * by this person ("Dana for Sofia" on the timeline and timesheet); the server's refusal is the toast.
 */
function FieldSteps({
  bp,
  visit,
  step,
  techName,
  busy,
  onArrive,
  onPause,
}: {
  bp: boolean;
  visit: BoardVisit;
  step: 'arrive' | 'pause';
  techName: (id: string) => string;
  busy: boolean;
  onArrive: (staffId: string) => void;
  onPause: (staffId: string, reason: PauseReason) => Promise<boolean>;
}) {
  const [pausing, setPausing] = useState<string | null>(null);
  const [reason, setReason] = useState<PauseReason | ''>('');
  if (bp)
    // Blueprint: each reason a Choice with its hint under it, then Pause and Cancel (Seth, 4 Nov: inline)
    return (
      <div className="bp-steps">
        {visit.techIds.map((id) =>
          step === 'arrive' ? (
            <ActionButton key={id} bp wide disabled={busy} onPress={() => onArrive(id)}>
              Arrived for {techName(id)}
            </ActionButton>
          ) : pausing === id ? (
            <div key={id} className="bp-pause" role="group" aria-label={`Pause for ${techName(id)}`}>
              <ChoiceGroup
                selectionMode="single"
                layout="stack"
                label={`Pause for ${techName(id)}`}
                value={reason}
                onChange={(v) => setReason(v as PauseReason)}
              >
                {PAUSE_REASONS.map(([k, label, hint]) => (
                  <Choice key={k} value={k} description={hint}>
                    {label}
                  </Choice>
                ))}
              </ChoiceGroup>
              <div className="bp-acts">
                <ActionButton
                  bp
                  kind="primary"
                  disabled={busy || !reason}
                  onPress={() =>
                    void (async () => {
                      if (!reason) return;
                      if (await onPause(id, reason)) setPausing(null);
                    })()
                  }
                >
                  {busy ? 'Pausing…' : `Pause for ${techName(id)}`}
                </ActionButton>
                <ActionButton bp kind="quiet" disabled={busy} onPress={() => setPausing(null)}>
                  Cancel
                </ActionButton>
              </div>
            </div>
          ) : (
            <ActionButton
              key={id}
              bp
              wide
              disabled={busy}
              onPress={() => {
                setPausing(id);
                setReason('');
              }}
            >
              Pause for {techName(id)}…
            </ActionButton>
          ),
        )}
      </div>
    );
  if (step === 'arrive')
    return (
      <div className="field-steps">
        {visit.techIds.map((id) => (
          <ActionButton key={id} bp={bp} wide disabled={busy} onPress={() => onArrive(id)}>
            Arrived for {techName(id)}
          </ActionButton>
        ))}
      </div>
    );
  return (
    <div className="field-steps">
      {visit.techIds.map((id) =>
        pausing === id ? (
          <fieldset key={id} className="pause-for">
            <legend>Pause for {techName(id)}</legend>
            {PAUSE_REASONS.map(([k, label, hint]) => (
              <label key={k} className="reason">
                <input
                  type="radio"
                  name={`pause-${visit.appointmentId}-${id}`}
                  value={k}
                  checked={reason === k}
                  onChange={() => setReason(k)}
                />
                <span>
                  <b>{label}</b> <span className="muted small">{hint}</span>
                </span>
              </label>
            ))}
            <div className="row">
              <ActionButton
                bp={bp}
                kind="primary"
                disabled={busy || !reason}
                onPress={() =>
                  void (async () => {
                    if (!reason) return;
                    if (await onPause(id, reason)) setPausing(null);
                  })()
                }
              >
                {busy ? 'Pausing…' : `Pause for ${techName(id)}`}
              </ActionButton>
              <ActionButton bp={bp} kind="quiet" disabled={busy} onPress={() => setPausing(null)}>
                Cancel
              </ActionButton>
            </div>
          </fieldset>
        ) : (
          <ActionButton
            key={id}
            bp={bp}
            wide
            disabled={busy}
            onPress={() => {
              setPausing(id);
              setReason('');
            }}
          >
            Pause for {techName(id)}…
          </ActionButton>
        ),
      )}
    </div>
  );
}

/** The lead and the helpers on a visit (QA-254): the lead is picked, helpers are added and removed. */
function TechPicker({
  visit,
  techs,
  busy,
  onAssign,
}: {
  visit: BoardVisit;
  techs: { id: string; name: string }[];
  busy: boolean;
  onAssign: (lead: string | null, helpers: string[]) => void;
}) {
  const [adding, setAdding] = useState(false);
  const lead = visit.leadTechId;
  const helpers = visit.techIds.filter((t) => t !== lead);
  const name = (id: string) => techs.find((t) => t.id === id)?.name ?? 'A technician';
  const locked = busy || visit.status === 'dispatched';
  return (
    <>
      <label>
        Technician
        <select
          className="field"
          value={lead ?? ''}
          disabled={locked}
          onChange={(e) => {
            const next = e.target.value || null;
            onAssign(next, next ? helpers.filter((h) => h !== next) : []);
          }}
        >
          <option value="">Choose a technician</option>
          {techs.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      {helpers.length > 0 && (
        <ul className="helpers">
          {helpers.map((h) => (
            <li key={h} className="row">
              <span>{name(h)} (helper)</span>
              <button
                className="btn quiet"
                disabled={locked}
                aria-label={`Remove ${name(h)} as helper`}
                onClick={() =>
                  onAssign(
                    lead,
                    helpers.filter((x) => x !== h),
                  )
                }
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {lead &&
        (adding ? (
          <label>
            Helper
            <select
              className="field"
              value=""
              disabled={locked}
              onChange={(e) => {
                if (e.target.value) onAssign(lead, [...helpers, e.target.value]);
                setAdding(false);
              }}
            >
              <option value="">Pick a helper…</option>
              {techs
                .filter((t) => !visit.techIds.includes(t.id))
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </select>
          </label>
        ) : (
          <button
            className="btn soft wide"
            disabled={locked || visit.techIds.length >= 6}
            onClick={() => setAdding(true)}
          >
            Add helper
          </button>
        ))}
    </>
  );
}

/** Move a visit to another day or window (QA-255), from the visit as this board last saw it. */
/**
 * BL-124: a drop that needs a look. What the drag found, and, when it leaves the time the customer
 * was given, which window they keep. Moving it texts nobody; telling them is the job's.
 */
/**
 * The check's words: a move's, a started visit's times, or (BL-158) the warning before a finished
 * visit's times change, which changes what's on its record.
 */
function checkWords(o: Outcome) {
  if (o.retime === 'over')
    return {
      title: 'Change a finished visit’s times?',
      confirm: 'Change the times',
      cancel: 'Leave them as they were',
      texted: () => 'Nobody is texted. The job’s history keeps the times it had.',
    };
  if (o.retime === 'started')
    return {
      title: 'Check the new times',
      confirm: 'Change the times',
      cancel: 'Leave them as they were',
      texted: () => 'Nobody is texted. The technician’s iPad shows the new times.',
    };
  return {
    title: 'Check the move',
    confirm: 'Move it',
    cancel: 'Leave it where it was',
    texted: (name: string) => `Nobody is texted. To tell ${name}, open the job.`,
  };
}

function CheckMove({
  visit,
  outcome,
  start,
  timeZone,
  busy,
  onCancel,
  onMove,
}: {
  visit: BoardVisit;
  outcome: Outcome;
  start: string;
  timeZone: string;
  busy: boolean;
  onCancel: () => void;
  /** Resolves once the save is answered: no answer keeps it open, for Try again (QA-848). */
  onMove: (window: { start: string; end: string } | null) => Promise<MoveResult>;
}) {
  // a started or finished visit's times (BL-158): its window is what the customer was given, kept
  const shifted = outcome.retime || insideWindow(visit, start) ? null : shiftedWindow(visit, start);
  const [keep, setKeep] = useState(false);
  const words = checkWords(outcome);
  // QA-848: while it's out the button says so and can't be pressed again; a long wait and no answer say so
  const [sending, setSending] = useState(false);
  const slow = useSlow(sending, SLOW_SAVE_MS);
  const [noAnswer, setNoAnswer] = useState(false);
  return (
    <div className="move check-move" role="group" aria-label={words.title}>
      <p className="check-head">{outcome.head}</p>
      <ul className="check-lines">
        {outcome.lines.map((l: NoteLine, i) => (
          <li key={i} className={l.ok === false ? 'bad' : l.ok ? 'ok' : 'unk'}>
            {l.text}
          </li>
        ))}
      </ul>
      {shifted && visit.arrivalWindow && (
        <fieldset className="check-window">
          <legend>The time {visit.customerName} was given</legend>
          <label>
            <input type="radio" name="check-window" checked={!keep} onChange={() => setKeep(false)} />
            Make it {windowText({ start: shifted.start, end: shifted.end, arrivalWindow: null }, timeZone)}
          </label>
          <label>
            <input type="radio" name="check-window" checked={keep} onChange={() => setKeep(true)} />
            Keep {windowText(visit, timeZone)}
          </label>
        </fieldset>
      )}
      <p className="small muted">{words.texted(visit.customerName)}</p>
      {sending && slow && (
        <p className="notice small" role="status">
          {STILL_SAVING}
        </p>
      )}
      {noAnswer && (
        <p className="notice warn small" role="alert">
          {noAnswerLine(visit, timeZone)}
        </p>
      )}
      <div className="row">
        {/* not "Don't move it": a name holding "move it" reads as a second Move it (QA-706 4) */}
        <button className="btn soft" onClick={onCancel}>
          {words.cancel}
        </button>
        <button
          className="btn primary"
          disabled={busy || sending}
          onClick={() => {
            setSending(true);
            setNoAnswer(false);
            void onMove(shifted && !keep ? shifted : null)
              .then((r) => setNoAnswer(r === 'no-answer'))
              .finally(() => setSending(false));
          }}
        >
          {sending ? 'Moving…' : noAnswer ? 'Try again' : words.confirm}
        </button>
      </div>
    </div>
  );
}

function MoveVisit({
  visit,
  date,
  timeZone,
  busy,
  rebook = false,
  onMove,
}: {
  visit: BoardVisit;
  date: string;
  timeZone: string;
  busy: boolean;
  /** Its day went by unattended (QA-764): the same move, said as rebooking it. */
  rebook?: boolean;
  /** Resolves to null once it moved, or to the refusal. */
  onMove: (input: {
    appointmentId: string;
    start: string;
    end: string;
    arrivalWindow: { start: string; end: string };
    rowVersion: number;
  }) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  // the visit as the panel opened on it (QA-693): the board re-reads live, but a move from this panel
  // is from what it showed, so someone else's change since is refused, not silently overwritten
  const [opened, setOpened] = useState(visit.rowVersion);
  const [refused, setRefused] = useState<{ text: string; stale: boolean } | null>(null);
  // Days from the company's today (QA-332): "Tomorrow" is the day after today, whatever day the board
  // shows; an earlier day than the visit's is fine, a past one isn't.
  const today = localToday(timeZone);
  const days = dayOptions(today, 14);
  const [day, setDay] = useState(date < today ? today : date);
  const nowH = hourNow(timeZone);
  // the window it's in now, so changing only the day keeps the customer's time (QA-843)
  const [win, setWin] = useState(() =>
    moveWindowIndex(visit, DAY_WINDOWS, (w) => windowStarted(day, today, w, nowH), timeZone),
  );
  if (!open)
    return (
      <button
        className="btn soft wide"
        disabled={busy}
        onClick={() => {
          setOpened(visit.rowVersion);
          setRefused(null);
          setOpen(true);
        }}
      >
        {rebook ? 'Rebook' : 'Move'}
      </button>
    );
  return (
    <div className="move">
      <label>
        Day
        <select className="field" value={day} onChange={(e) => setDay(e.target.value)}>
          {days.map((d) => (
            <option key={d.date} value={d.date}>
              {d.date === date ? `${d.label} (where it is now)` : d.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        Arrival window
        <select className="field" value={win} onChange={(e) => setWin(Number(e.target.value))}>
          {DAY_WINDOWS.map((w, i) => (
            <option key={i} value={i} disabled={windowStarted(day, today, w, nowH)}>
              {windowLabel(w)}
              {windowStarted(day, today, w, nowH) ? ' (already started)' : ''}
            </option>
          ))}
        </select>
      </label>
      {refused && (
        <p className="notice warn small" role="alert">
          {refused.text}
        </p>
      )}
      <div className="row">
        <button className="btn soft" onClick={() => setOpen(false)}>
          Cancel
        </button>
        {refused?.stale && (
          <button
            className="btn soft"
            disabled={busy}
            onClick={() => {
              // the board has read it again: move from this version now, what was picked kept
              setOpened(visit.rowVersion);
              setRefused({
                text: sentences(
                  `This is the latest now: ${windowText(visit, timeZone)}`,
                  'What you picked is kept: check it, then move it again',
                ),
                stale: false,
              });
            }}
          >
            Get the latest
          </button>
        )}
        <button
          className="btn primary"
          disabled={busy || windowStarted(day, today, DAY_WINDOWS[win]!, nowH)}
          onClick={() => {
            const [s0, e0] = DAY_WINDOWS[win]!;
            const length = Date.parse(visit.end) - Date.parse(visit.start);
            const start = zonedInstant(day, s0, timeZone);
            void onMove({
              appointmentId: visit.appointmentId,
              start,
              end: new Date(Date.parse(start) + Math.max(length, (e0 - s0) * 3_600_000)).toISOString(),
              arrivalWindow: { start, end: zonedInstant(day, e0, timeZone) },
              rowVersion: opened,
            }).then((e) => {
              if (!e) return setOpen(false);
              setRefused({
                text: e instanceof RpcError ? e.message : 'That did not work. Try again.',
                stale: isStale(e),
              });
            });
          }}
        >
          Move visit
        </button>
      </div>
    </div>
  );
}

interface RetimeProps {
  board: Board;
  visit: BoardVisit;
  rule: 'started' | 'over';
  now: string;
  timeZone: string;
  busy: boolean;
  /** Resolves to null once the times changed, or to the refusal. */
  onRetime: (input: { start: string; end: string; confirm?: true; rowVersion: number }) => Promise<unknown>;
}

/** What Change times says first (BL-158): the warning for a finished visit, the rule while work goes on. */
function retimeLede(rule: 'started' | 'over', v: BoardVisit, now: string, tz: string): string {
  if (rule === 'over')
    return v.status === 'closed' || v.pauseReason === 'work_done'
      ? `${v.customerName} is done. Changing its times changes what’s on its record.`
      : `${v.customerName}’s day has gone by. Changing its times changes what’s on its record.`;
  return `Work is going on at ${v.customerName}: it can end ${timeText(now, tz)} at the earliest. Same technician, same day.`;
}

/** Change times on Riverstone (BL-158): the start and end typed, checked as a drag is, on its own day. */
function RetimeVisit({ board, visit, rule, now, timeZone, busy, onRetime }: RetimeProps) {
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(visit.rowVersion);
  const [text, setText] = useState({ start: '', end: '' });
  const [refused, setRefused] = useState<string | null>(null);
  const check = typedTimes(board, visit, text, now, timeZone);
  if (!open)
    return (
      <button
        className="btn soft wide"
        disabled={busy}
        onClick={() => {
          setOpened(visit.rowVersion);
          setText({ start: timeText(visit.start, timeZone), end: timeText(visit.end, timeZone) });
          setRefused(null);
          setOpen(true);
        }}
      >
        Change times
      </button>
    );
  const err = check.errors.start ?? check.errors.end ?? refused;
  return (
    <div className="move" role="group" aria-label="Change the times">
      <p className={rule === 'over' ? 'notice warn small' : 'small muted'}>
        {retimeLede(rule, visit, now, timeZone)}
      </p>
      <label>
        Started
        <input
          className="field"
          value={text.start}
          onChange={(e) => setText({ ...text, start: e.target.value })}
        />
      </label>
      <label>
        Ends
        <input
          className="field"
          value={text.end}
          onChange={(e) => setText({ ...text, end: e.target.value })}
        />
      </label>
      {err && (
        <p className="notice warn small" role="alert">
          {err}
        </p>
      )}
      {check.outcome?.lines
        .filter((l) => l.ok === false && rule !== 'over')
        .map((l, i) => (
          <p key={i} className="small muted">
            {l.text}
          </p>
        ))}
      <div className="row">
        <button className="btn soft" onClick={() => setOpen(false)}>
          Cancel
        </button>
        <button
          className="btn primary"
          disabled={busy || !check.to || check.outcome?.kind === 'none'}
          onClick={() => {
            if (!check.to) return;
            void onRetime({
              ...check.to,
              ...(rule === 'over' ? { confirm: true as const } : {}),
              rowVersion: opened,
            }).then((e) => {
              if (!e) return setOpen(false);
              setRefused(e instanceof RpcError ? e.message : 'That did not work. Try again.');
            });
          }}
        >
          {rule === 'over' ? 'Change them anyway' : 'Change times'}
        </button>
      </div>
    </div>
  );
}

// ── Blueprint's: the same three drawn with the shared library (Field, FormDialog, ConfirmDialog) ──

/** TechPicker on Blueprint: the lead in a SelectField; helpers listed, each with Remove, and one more added. */
function BpTechPicker({
  visit,
  techs,
  busy,
  onAssign,
}: {
  visit: BoardVisit;
  techs: { id: string; name: string }[];
  busy: boolean;
  onAssign: (lead: string | null, helpers: string[]) => void;
}) {
  const [adding, setAdding] = useState(false);
  const lead = visit.leadTechId;
  const helpers = visit.techIds.filter((t) => t !== lead);
  const name = (id: string) => techs.find((t) => t.id === id)?.name ?? 'A technician';
  const locked = busy || visit.status === 'dispatched';
  return (
    <>
      <SelectField
        label="Technician"
        value={lead ?? ''}
        isDisabled={locked}
        options={[
          { value: '', label: 'Choose a technician' },
          ...techs.map((t) => ({ value: t.id, label: t.name })),
        ]}
        onChange={(v) => {
          const next = v || null;
          onAssign(next, next ? helpers.filter((h) => h !== next) : []);
        }}
      />
      {helpers.length > 0 && (
        <ul className="helpers">
          {helpers.map((h) => (
            <li key={h} className="row">
              <span>{name(h)} (helper)</span>
              <FlLink
                isDisabled={locked}
                onPress={() =>
                  onAssign(
                    lead,
                    helpers.filter((x) => x !== h),
                  )
                }
              >
                Remove {name(h)}
              </FlLink>
            </li>
          ))}
        </ul>
      )}
      {lead &&
        (adding ? (
          <SelectField
            label="Helper"
            value=""
            isDisabled={locked}
            options={[
              { value: '', label: 'Pick a helper…' },
              ...techs
                .filter((t) => !visit.techIds.includes(t.id))
                .map((t) => ({ value: t.id, label: t.name })),
            ]}
            onChange={(v) => {
              if (v) onAssign(lead, [...helpers, v]);
              setAdding(false);
            }}
          />
        ) : (
          <FlButton
            isDisabled={locked || visit.techIds.length >= 6}
            size="touch-on-phone"
            onPress={() => setAdding(true)}
          >
            Add helper
          </FlButton>
        ))}
    </>
  );
}

/**
 * A change sent from a dialog while the connection is down (QA-767): it's kept and goes when the
 * connection is back (the client sends it then), so the dialog says so rather than nothing, until it
 * closes as it does once the change is in.
 */
function OfflineWaiting({ sending }: { sending: boolean }) {
  const online = useOnline();
  const [said, setSaid] = useState(false);
  useEffect(() => {
    if (!sending) setSaid(false);
    else if (!online) setSaid(true);
  }, [sending, online]);
  return said ? (
    <p className="bp-say" role="status">
      {OFFLINE_WAITING}
    </p>
  ) : null;
}

/** CheckMove on Blueprint (BL-124): the drop that needs a look, as one decision in a ConfirmDialog. */
function BpCheckMove({
  visit,
  outcome,
  start,
  timeZone,
  onCancel,
  onMove,
}: {
  visit: BoardVisit;
  outcome: Outcome;
  start: string;
  timeZone: string;
  onCancel: () => void;
  /** Resolves once the save is answered: no answer keeps it open, for Try again (QA-848). */
  onMove: (window: { start: string; end: string } | null) => Promise<MoveResult>;
}) {
  // a started or finished visit's times (BL-158): its window is what the customer was given, kept
  const shifted = outcome.retime || insideWindow(visit, start) ? null : shiftedWindow(visit, start);
  const [keep, setKeep] = useState('make');
  // closing after Move it isn't "leave it": only Close, Escape or the second button leaves it
  const moved = useRef(false);
  // sent: the board closes it once the move is in; offline, it says it's kept until then (QA-767)
  const [sending, setSending] = useState(false);
  // QA-848: while it's out the button is busy; a long wait says so; no answer says so, with Try again
  const slow = useSlow(sending, SLOW_SAVE_MS);
  const online = useOnline();
  const [noAnswer, setNoAnswer] = useState(false);
  const words = checkWords(outcome);
  return (
    <ConfirmDialog
      isOpen
      title={words.title}
      confirmLabel={sending ? 'Moving…' : noAnswer ? 'Try again' : words.confirm}
      // not "Don't move it": a name holding "move it" reads as a second Move it (QA-706 4)
      cancelLabel={words.cancel}
      onClose={() => {
        if (!moved.current) onCancel();
      }}
      onConfirm={async () => {
        if (moved.current) return; // once: a second press would send it twice
        moved.current = true;
        setSending(true);
        const r = await onMove(shifted && keep === 'make' ? shifted : null).finally(() => setSending(false));
        if (r !== 'no-answer') return;
        moved.current = false;
        setNoAnswer(true);
        throw new Error(noAnswerLine(visit, timeZone));
      }}
    >
      <OfflineWaiting sending={sending} />
      {sending && slow && online && <p role="status">{STILL_SAVING}</p>}
      <p className="check-head">{outcome.head}</p>
      <ul className="check-lines">
        {outcome.lines.map((l: NoteLine, i) => (
          <li key={i} className={l.ok === false ? 'bad' : l.ok ? 'ok' : 'unk'}>
            {l.text}
          </li>
        ))}
      </ul>
      {shifted && visit.arrivalWindow && (
        <ChoiceGroup
          selectionMode="single"
          label={`The time ${visit.customerName} was given`}
          value={keep}
          onChange={setKeep}
        >
          <Choice value="make">
            Make it {windowText({ start: shifted.start, end: shifted.end, arrivalWindow: null }, timeZone)}
          </Choice>
          <Choice value="keep">Keep {windowText(visit, timeZone)}</Choice>
        </ChoiceGroup>
      )}
      <p>{words.texted(visit.customerName)}</p>
    </ConfirmDialog>
  );
}

/** MoveVisit on Blueprint (QA-255, QA-693): Move opens a FormDialog with the day and the window. */
function BpMoveVisit({
  visit,
  date,
  timeZone,
  busy,
  rebook = false,
  onMove,
}: {
  visit: BoardVisit;
  date: string;
  timeZone: string;
  busy: boolean;
  /** Its day went by unattended (QA-764): the same move, said as rebooking it. */
  rebook?: boolean;
  onMove: (input: {
    appointmentId: string;
    start: string;
    end: string;
    arrivalWindow: { start: string; end: string };
    rowVersion: number;
  }) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(visit.rowVersion);
  const [stale, setStale] = useState(false);
  const [latest, setLatest] = useState<string | null>(null);
  const today = localToday(timeZone);
  const days = dayOptions(today, 14);
  const nowH = hourNow(timeZone);
  // the window it's in now, so changing only the day keeps the customer's time (QA-843)
  const firstWindow = (d: string) =>
    moveWindowIndex(visit, DAY_WINDOWS, (w) => windowStarted(d, today, w, nowH), timeZone);
  const startDay = date < today ? today : date;
  const [day, setDay] = useState(startDay);
  const [win, setWin] = useState(() => firstWindow(startDay));
  const started = windowStarted(day, today, DAY_WINDOWS[win]!, nowH);
  const [tried, setTried] = useState(false);
  const [sending, setSending] = useState(false);
  return (
    <>
      <FlButton
        isDisabled={busy}
        size="touch-on-phone"
        aria-haspopup="dialog"
        onPress={() => {
          setOpened(visit.rowVersion);
          setStale(false);
          setLatest(null);
          setTried(false);
          setDay(startDay);
          setWin(firstWindow(startDay));
          setOpen(true);
        }}
      >
        {rebook ? 'Rebook' : 'Move'}
      </FlButton>
      <FormDialog
        isOpen={open}
        onClose={() => setOpen(false)}
        title={rebook ? 'Rebook the visit' : 'Move the visit'}
        submitLabel={rebook ? 'Rebook visit' : 'Move visit'}
        isDirty={day !== startDay || win !== firstWindow(startDay)}
        validate={() => {
          setTried(true);
          return !started;
        }}
        onSubmit={async () => {
          const [s0, e0] = DAY_WINDOWS[win]!;
          const length = Date.parse(visit.end) - Date.parse(visit.start);
          const start = zonedInstant(day, s0, timeZone);
          setSending(true);
          const e = await onMove({
            appointmentId: visit.appointmentId,
            start,
            end: new Date(Date.parse(start) + Math.max(length, (e0 - s0) * 3_600_000)).toISOString(),
            arrivalWindow: { start, end: zonedInstant(day, e0, timeZone) },
            rowVersion: opened,
          }).finally(() => setSending(false));
          if (!e) return;
          setStale(isStale(e));
          setLatest(null);
          throw new Error(refusalWords(e));
        }}
      >
        <SelectField
          label="Day"
          value={day}
          onChange={(d) => {
            setDay(d);
            if (windowStarted(d, today, DAY_WINDOWS[win]!, nowH)) setWin(firstWindow(d));
          }}
          options={days.map((d) => ({
            value: d.date,
            label: d.date === date ? `${d.label} (where it is now)` : d.label,
          }))}
        />
        <SelectField
          label="Arrival window"
          value={String(win)}
          onChange={(v) => setWin(Number(v))}
          error={tried && started ? 'That window has already started. Pick a later one.' : null}
          // a window that has started can't be picked (the classic board's disabled options), so Move
          // never refuses what the list offered
          options={DAY_WINDOWS.map((w, i) => {
            const gone = windowStarted(day, today, w, nowH);
            return {
              value: String(i),
              label: `${windowLabel(w)}${gone ? ' (already started)' : ''}`,
              ...(gone ? { isDisabled: true } : {}),
            };
          })}
        />
        {latest && <p role="status">{latest}</p>}
        <OfflineWaiting sending={sending} />
        {stale && (
          <FlLink
            onPress={() => {
              // the board has read it again: move from this version now, what was picked kept
              setOpened(visit.rowVersion);
              setStale(false);
              setLatest(
                sentences(
                  `This is the latest now: ${windowText(visit, timeZone)}`,
                  'What you picked is kept: check it, then move it again',
                ),
              );
            }}
          >
            Get the latest
          </FlLink>
        )}
      </FormDialog>
    </>
  );
}

/** Change times on Blueprint (BL-158): a FormDialog with the start and end, the warning first when it's over. */
function BpRetimeVisit({ board, visit, rule, now, timeZone, busy, onRetime }: RetimeProps) {
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(visit.rowVersion);
  const was = { start: timeText(visit.start, timeZone), end: timeText(visit.end, timeZone) };
  const [text, setText] = useState(was);
  const [tried, setTried] = useState(false);
  // each field is checked as it's left (QA-766), and both on Save
  const [left, setLeft] = useState<{ start?: boolean; end?: boolean }>({});
  const [sending, setSending] = useState(false);
  const check = typedTimes(board, visit, text, now, timeZone);
  const refused = check.outcome?.kind === 'refuse';
  // a refusal sits on its field (QA-766), so it isn't said a second time under them
  const looks = refused ? [] : (check.outcome?.lines ?? []).filter((l) => l.ok === false && rule !== 'over');
  const errorOn = (f: 'start' | 'end') => (tried || left[f] || refused ? (check.errors[f] ?? null) : null);
  return (
    <>
      <FlButton
        isDisabled={busy}
        size="touch-on-phone"
        aria-haspopup="dialog"
        onPress={() => {
          setOpened(visit.rowVersion);
          setText(was);
          setTried(false);
          setLeft({});
          setOpen(true);
        }}
      >
        Change times
      </FlButton>
      <FormDialog
        isOpen={open}
        onClose={() => setOpen(false)}
        title={rule === 'over' ? 'Change a finished visit’s times?' : 'Change the times'}
        submitLabel={rule === 'over' ? 'Change them anyway' : 'Change times'}
        isDirty={text.start !== was.start || text.end !== was.end}
        isSubmitDisabled={check.outcome?.kind === 'none'}
        note={retimeLede(rule, visit, now, timeZone)}
        validate={() => {
          setTried(true);
          return !!check.to;
        }}
        onSubmit={async () => {
          if (!check.to) return;
          setSending(true);
          const e = await onRetime({
            ...check.to,
            ...(rule === 'over' ? { confirm: true as const } : {}),
            rowVersion: opened,
          }).finally(() => setSending(false));
          if (e) throw new Error(refusalWords(e));
        }}
      >
        <TextField
          label="Started"
          value={text.start}
          onChange={(v) => setText({ ...text, start: v })}
          onBlur={() => setLeft((l) => ({ ...l, start: true }))}
          error={errorOn('start')}
        />
        <TextField
          label="Ends"
          value={text.end}
          onChange={(v) => setText({ ...text, end: v })}
          onBlur={() => setLeft((l) => ({ ...l, end: true }))}
          error={errorOn('end')}
        />
        <OfflineWaiting sending={sending} />
        {looks.map((l, i) => (
          <p key={i}>{l.text}</p>
        ))}
      </FormDialog>
    </>
  );
}
